import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ProcessingStatus from './ProcessingStatus';
import LegacyCSVImport from './LegacyCSVImport';
import { ProcessorStatePanel, useProcessorStates } from './ProcessorStateBadge';
import { useAiSwitch } from './curation/automation/useAiSwitch';
import { AutomationSwitchNotice, switchMoved } from './processors/AutomationSwitchNotice';

interface ProcessorStatusResponse {
  isEnabled: boolean;
  minEmailAgeDays?: number;
  stats?: {
    totalPending: number;
    processedToday: number;
    lastRunTime?: string;
  };
}

interface AutoLocPreview {
  totalEligible: number;
  withValidEmails: number;
  withoutEmails: number;
  maxPerRun: number;
  nextBatchSize: number;
}

interface ProcessorStatus {
  name: string;
  enabled: boolean;
  description?: string;
  configuration?: {
    rateLimit?: string;
    minAgeDays?: number;
    [key: string]: any;
  };
}

// Cosmos-backed processor state doc (subset we render).
interface ProcessorStateDoc {
  id: string;
  desiredState: 'running' | 'stopped';
  actualState: 'idle' | 'starting' | 'running' | 'finishing' | 'crashed' | 'stopped';
  ownerInstanceId: string | null;
  ownerBootId: string | null;
  startedAt: string | null;
  startedBy: string | null;
  lastHeartbeatAt: string | null;
  currentPhase: string | null;
  lastRunStats: Record<string, unknown> | null;
  /** Per-RUN: release_lease overwrites it (with null) on every clean finish. */
  lastError: string | null;
  /** STANDING condition (e.g. CAF_SPLIT_CUTOVER unset) — written only by
   *  processor_state_service.note_warning, never cleared by the lease
   *  lifecycle. Older docs predate the field. */
  lastWarning?: string | null;
  lastUpdatedAt: string;
}

interface UnknownNamesFixProgress {
  running: boolean;
  total: number;
  processed: number;
  fixed: number;
  no_claims: number;
  no_name_found: number;
  errors: number;
  current_client: string;
  start_time?: string;
  end_time?: string;
  last_error?: string;
}

// Feature flag: 4 legacy processor sections are hidden in the UI (retired
// pending ownership review). Typed as ``boolean`` so TS still narrows
// inner conditionals inside the hidden JSX.
const SHOW_RETIRED_PROCESSOR_SECTIONS: boolean = false;

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'unknown';
  const diffSecs = Math.round((Date.now() - ts) / 1000);
  if (diffSecs < 0) return 'in future';
  if (diffSecs < 5) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  const mins = Math.floor(diffSecs / 60);
  if (mins < 60) return `${mins}m ${diffSecs % 60}s ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return new Date(iso).toLocaleString();
}

const ProcessorsManagement: React.FC = () => {
  const { apiRequest } = useAuth();

  // Cosmos-backed heartbeat state for all 9 lease-pattern processors.
  // Polled every 10s; each card reads its own state via processorStates[id].
  const processorStates = useProcessorStates();
  const aiSwitch = useAiSwitch(true);
  const cafSwitchMoved = switchMoved(aiSwitch);

  // Email Cancellation Processor state
  const [isProcessorEnabled, setIsProcessorEnabled] = useState<boolean>(false);
  const [emailProcessorDays, setEmailProcessorDays] = useState<number>(3);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [runningProcessorNow, setRunningProcessorNow] = useState<boolean>(false);

  // CAF Inbox Processor state
  const [isCAFInboxProcessorEnabled, setIsCAFInboxProcessorEnabled] = useState<boolean>(false);
  const [cafInboxStatusMessage, setCAFInboxStatusMessage] = useState<string>('');
  const [cafInboxMode, setCAFInboxMode] = useState<'regular' | 'test' | 'runonce'>('test');
  const [cafBatchRunning, setCafBatchRunning] = useState<boolean>(false);
  // Cosmos-backed consolidated state (pilot).
  const [cafInboxState, setCafInboxState] = useState<ProcessorStateDoc | null>(null);
  const [cafInboxStateError, setCafInboxStateError] = useState<string>('');
  const [showCafLastRunStats, setShowCafLastRunStats] = useState<boolean>(false);

  // DSAR Inbox Processor state
  const [isDSARInboxProcessorEnabled, setIsDSARInboxProcessorEnabled] = useState<boolean>(false);
  const [dsarInboxStatusMessage, setDSARInboxStatusMessage] = useState<string>('');
  const [dsarInboxMode, setDSARInboxMode] = useState<'regular' | 'single'>('single');

  // Automated LOC Sender state
  const [isAutoLocSenderEnabled, setIsAutoLocSenderEnabled] = useState<boolean>(false);
  const [autoLocStatusMessage, setAutoLocStatusMessage] = useState<string>('');
  const [autoLocPreview, setAutoLocPreview] = useState<AutoLocPreview | null>(null);

  // Photo ID Processor state
  const [isIdProcessorEnabled, setIsIdProcessorEnabled] = useState<boolean>(false);
  const [idProcessorStatus, setIdProcessorStatus] = useState<string>('');
  const [idProcessorRunning, setIdProcessorRunning] = useState<boolean>(false);
  const [idProcessorStats, setIdProcessorStats] = useState<{
    totalPending: number;
    processedToday: number;
    lastRunTime?: string;
  } | null>(null);

  // Full backlog state
  const [backlogJobId, setBacklogJobId] = useState<string | null>(null);
  const [backlogProgress, setBacklogProgress] = useState<{
    status: string;
    message: string;
    sent: number;
    failed: number;
    skipped: number;
    total: number;
    percentage: number;
  } | null>(null);

  // General processors status state
  const [processorsStatus, setProcessorsStatus] = useState<ProcessorStatus[]>([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);
  
  // Recent Claims Processor state
  const [isRecentClaimsEnabled, setIsRecentClaimsEnabled] = useState<boolean>(false);
  const [recentClaimsStatus, setRecentClaimsStatus] = useState<string>('');
  const [recentClaimsRunning, setRecentClaimsRunning] = useState<boolean>(false);
  const [recentClaimsStats, setRecentClaimsStats] = useState<{
    pendingClaims: number;
    lastRun?: {
      time: string;
      results?: any;
    };
    nextRun?: string;
    lookbackMinutes: number;
    intervalMinutes: number;
  } | null>(null);
  
  // ID Verification AI state
  const [isIdVerificationAIEnabled, setIsIdVerificationAIEnabled] = useState<boolean>(false);
  const [idVerificationAIStatus, setIdVerificationAIStatus] = useState<string>('');
  const [idVerificationAIRunning, setIdVerificationAIRunning] = useState<boolean>(false);
  const [idVerificationAIStats, setIdVerificationAIStats] = useState<{
    totalProcessedByAI: number;
    totalApprovedByAI: number;
    pendingTasks: number;
    approvalRate: number;
  } | null>(null);

  // Claims Sync Processor state
  const [isClaimsSyncEnabled, setIsClaimsSyncEnabled] = useState<boolean>(false);
  const [claimsSyncStatus, setClaimsSyncStatus] = useState<string>('');
  const [claimsSyncRunning, setClaimsSyncRunning] = useState<boolean>(false);
  const [claimsSyncStats, setClaimsSyncStats] = useState<{
    totalClaims: number;
    syncedClaims: number;
    unsyncedClaims: number;
    normalizedRecords: number;
    syncPercentage: number;
  } | null>(null);

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(false);
  
  // Unknown names fix state
  const [unknownNamesFixProgress, setUnknownNamesFixProgress] = useState<UnknownNamesFixProgress | null>(null);
  const [unknownNamesCount, setUnknownNamesCount] = useState<number | null>(null);
  const [isCountingUnknowns, setIsCountingUnknowns] = useState<boolean>(false);

  // Cancelled Claims Sync Processor state
  const [isCancelledClaimsSyncEnabled, setIsCancelledClaimsSyncEnabled] = useState<boolean>(false);
  const [cancelledClaimsSyncStatus, setCancelledClaimsSyncStatus] = useState<string>('');
  const [cancelledClaimsSyncRunning, setCancelledClaimsSyncRunning] = useState<boolean>(false);
  const [cancelledClaimsSyncStats, setCancelledClaimsSyncStats] = useState<{
    pendingCount: number;
    syncedCount: number;
    lastRunTime?: string;
    syncPercentage: number;
  } | null>(null);

  // Client XML Export Processor state
  const [isClientXMLExportEnabled, setIsClientXMLExportEnabled] = useState<boolean>(false);
  const [clientXMLExportStatus, setClientXMLExportStatus] = useState<string>('');
  const [clientXMLExportRunning, setClientXMLExportRunning] = useState<boolean>(false);
  const [clientXMLExportTestRunning, setClientXMLExportTestRunning] = useState<boolean>(false);
  const [clientXMLExportStats, setClientXMLExportStats] = useState<{
    pendingExport: number;
    exportedClients: number;
    totalClients: number;
    lastRunTime?: string;
    exportPercentage: number;
  } | null>(null);

  // Post Document Processor state
  const [isPostDocumentProcessorEnabled, setIsPostDocumentProcessorEnabled] = useState<boolean>(false);
  const [postDocumentStatus, setPostDocumentStatus] = useState<string>('');
  const [postDocumentRunning, setPostDocumentRunning] = useState<boolean>(false);
  const [postDocumentPreviewing, setPostDocumentPreviewing] = useState<boolean>(false);
  const [postDocumentVisionRunning, setPostDocumentVisionRunning] = useState<boolean>(false);
  const [postDocumentStats, setPostDocumentStats] = useState<{
    pendingDocuments: number;
    lastRun?: {
      time: string;
      results?: any;
    };
    nextRun?: string;
    intervalMinutes: number;
  } | null>(null);

  // Call Tasks Processor state
  const [isCallTasksProcessorEnabled, setIsCallTasksProcessorEnabled] = useState<boolean>(false);
  const [callTasksProcessorStatus, setCallTasksProcessorStatus] = useState<string>('');
  const [callTasksProcessorLoading, setCallTasksProcessorLoading] = useState<boolean>(false);
  const [callTasksProcessorStats, setCallTasksProcessorStats] = useState<{
    enabled: boolean;
    intervalMinutes: number;
    nextRunTime?: string;
    unprocessedFilesCount: number;
  } | null>(null);

  // CAF Backlog Processor state
  const [isCafBacklogProcessorEnabled, setIsCafBacklogProcessorEnabled] = useState<boolean>(false);
  const [cafBacklogProcessorStatus, setCafBacklogProcessorStatus] = useState<string>('');
  const [cafBacklogProcessorLoading, setCafBacklogProcessorLoading] = useState<boolean>(false);
  const [cafBacklogBatchSize, setCafBacklogBatchSize] = useState<number>(100);
  const [cafBacklogStats, setCafBacklogStats] = useState<{
    enabled: boolean;
    batchSize: number;
    lastRun: {
      time: string | null;
      results: {
        totalProcessed: number;
        totalSuccess: number;
        totalErrors: number;
        claimsMatched: number;
        tasksArchived: number;
        tasksAssigned: number;
        tasksCompleted: number;
        keyDatesUpdated: number;
        actionCounts: {
          AUTO_ARCHIVE: number;
          AUTO_ASSIGN_COMPLETE: number;
          AUTO_ASSIGN_ONLY: number;
          MANUAL_REVIEW: number;
        };
        totalTokens: number;
        processingTimeMs: number;
      } | null;
    };
    nextRun: string | null;
    pendingTasks: number;
    jobActive: boolean;
  } | null>(null);
  // CAF Haiku Backfill state
  const [haikuBackfillStatus, setHaikuBackfillStatus] = useState<any>(null);
  const [haikuBackfillLoading, setHaikuBackfillLoading] = useState(false);
  // Post Backlog Processor state
  const [isPostBacklogProcessorEnabled, setIsPostBacklogProcessorEnabled] = useState<boolean>(false);
  const [postBacklogProcessorStatus, setPostBacklogProcessorStatus] = useState<string>('');
  const [postBacklogProcessorLoading, setPostBacklogProcessorLoading] = useState<boolean>(false);
  const [postBacklogBatchSize, setPostBacklogBatchSize] = useState<number>(100);
  const [postBacklogDryRunResults, setPostBacklogDryRunResults] = useState<Array<{
    id: string;
    status: string;
    oldCategory: string | null;
    oldConfidence: number | null;
    newCategory: string | null;
    newConfidence: number | null;
    newSource: string;
    belowThreshold: boolean;
    changed: boolean;
    reasoning: string;
    error?: string;
  }> | null>(null);
  const [postBacklogStats, setPostBacklogStats] = useState<{
    enabled: boolean;
    totalTasks: number;
    pendingTasks: number;
    lastRun: {
      time: string | null;
      results: {
        totalProcessed: number;
        totalSuccess: number;
        totalErrors: number;
        categoryChanged: number;
        automationsTriggered: number;
        belowThresholdCount: number;
        categoryCounts: Record<string, number>;
        processingTimeMs: number;
      } | null;
    };
    jobActive: boolean;
  } | null>(null);

  // CAF Embedding Processor state (PR 5c — wraps caf_email_embedding_job)
  const [isCafEmbeddingEnabled, setIsCafEmbeddingEnabled] = useState<boolean>(false);
  const [cafEmbeddingStatusMessage, setCafEmbeddingStatusMessage] = useState<string>('');
  const [cafEmbeddingLoading, setCafEmbeddingLoading] = useState<boolean>(false);
  const [cafEmbeddingStats, setCafEmbeddingStats] = useState<{
    enabled: boolean;
    batchSize: number;
    intervalSeconds: number;
    lastRun: {
      time: string | null;
      // The job returns a loose dict; keep this open-shaped on purpose.
      results: Record<string, unknown> | null;
    };
    nextRun: string | null;
    jobActive: boolean;
  } | null>(null);

  // CAF Corpus Cluster Processor state (PR 5c — wraps caf_corpus_cluster_job)
  const [isCafCorpusClusterEnabled, setIsCafCorpusClusterEnabled] = useState<boolean>(false);
  const [cafCorpusClusterStatusMessage, setCafCorpusClusterStatusMessage] = useState<string>('');
  const [cafCorpusClusterLoading, setCafCorpusClusterLoading] = useState<boolean>(false);
  const [cafCorpusClusterStats, setCafCorpusClusterStats] = useState<{
    enabled: boolean;
    intervalSeconds: number;
    cosineThreshold: number;
    minClusterSize: number;
    lastRun: {
      time: string | null;
      results: Record<string, unknown> | null;
    };
    nextRun: string | null;
    jobActive: boolean;
  } | null>(null);

  const [clawbackLoading, setClawbackLoading] = useState<boolean>(false);
  const [clawbackResults, setClawbackResults] = useState<{
    duplicateClaimsFound: number;
    tasksReset: number;
    dryRun: boolean;
    sampleMatches: Array<{
      id: string;
      category: string;
      claimRef: string;
      emailSubject: string;
      emailFrom: string;
    }>;
  } | null>(null);

  const [reprocessStuckLoading, setReprocessStuckLoading] = useState<boolean>(false);
  const [reprocessStuckResults, setReprocessStuckResults] = useState<{
    totalInProgress: number;
    inScope: number;
    humanAssignedSkipped: number;
    tasksReset: number;
    dryRun: boolean;
    preserveHumanAssigned: boolean;
    sample: Array<{
      id: string;
      aiCategory: string | null;
      aiAction: string | null;
      aiConfidence: number | null;
      assignedClaimId: string | null;
      assignedBy: string | null;
    }>;
  } | null>(null);

  useEffect(() => {
    fetchAllProcessorStatuses();
    // fetchUnknownNamesCount(); -- section hidden
    // Poll CAF inbox Cosmos-backed state every 10s for the new status card.
    fetchCafInboxState();
    const cafStateInterval = setInterval(fetchCafInboxState, 10_000);
    return () => clearInterval(cafStateInterval);
  }, [apiRequest]);

  useEffect(() => {
    if (!haikuBackfillStatus?.running) return;
    const interval = setInterval(fetchHaikuBackfillStatus, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haikuBackfillStatus?.running]);

  const fetchAllProcessorStatuses = async () => {
    fetchProcessorStatus();
    fetchCAFInboxProcessorStatus();
    fetchDSARInboxProcessorStatus();
    fetchAutoLocSenderStatus();
    fetchAutoLocSenderPreview();
    fetchIdProcessorStatus();
    fetchGeneralProcessorsStatus();
    fetchRecentClaimsProcessorStatus();
    fetchIdVerificationAIStatus();
    fetchClaimsSyncStatus();
    // fetchCancelledClaimsSyncStatus(); -- section hidden
    fetchClientXMLExportStatus();
    fetchPostDocumentStatus();
    // fetchCallTasksProcessorStatus(); -- section hidden
    fetchCafBacklogProcessorStatus();
    fetchHaikuBackfillStatus();
    fetchPostBacklogProcessorStatus();
    fetchCafEmbeddingProcessorStatus();
    fetchCafCorpusClusterProcessorStatus();
  };

  const fetchProcessorStatus = async () => {
    try {
      const response = await apiRequest<ProcessorStatusResponse & { minEmailAgeDays?: number }>({
        url: '/ui/admin/cancellation-processor',
        method: 'GET'
      });
      setIsProcessorEnabled(response.isEnabled);
      if (response.minEmailAgeDays) {
        setEmailProcessorDays(response.minEmailAgeDays);
      }
    } catch (error) {
      console.error('Error fetching processor status:', error);
      setStatusMessage('Failed to fetch processor status');
    }
  };

  const toggleProcessor = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/cancellation-processor/${endpoint}`,
        method: 'POST'
      });
      setIsProcessorEnabled(enable);
      setStatusMessage(`Processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} processor:`, error);
      setStatusMessage(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    }
  };

  const runProcessorNow = async () => {
    try {
      setRunningProcessorNow(true);
      setStatusMessage('');
      await apiRequest({
        url: '/ui/admin/cancellation-processor/run',
        method: 'POST'
      });
      setStatusMessage('Cancellation processor executed.');
      fetchProcessorStatus();
    } catch (error) {
      console.error('Error running cancellation processor:', error);
      setStatusMessage('Failed to run cancellation processor');
    } finally {
      setRunningProcessorNow(false);
    }
  };

  const updateProcessorConfig = async () => {
    try {
      await apiRequest({
        url: '/ui/admin/cancellation-processor/config',
        method: 'POST',
        data: { minEmailAgeDays: emailProcessorDays }
      });
      setStatusMessage(`Email processor configured to process emails ${emailProcessorDays}+ days old`);
    } catch (error) {
      console.error('Error updating processor config:', error);
      setStatusMessage('Failed to update processor configuration');
    }
  };

  const fetchCAFInboxProcessorStatus = async () => {
    try {
      const response = await apiRequest<{ isEnabled: boolean }>({
        url: '/ui/admin/caf-inbox-processor',
        method: 'GET',
      });
      setIsCAFInboxProcessorEnabled(response.isEnabled);
    } catch (error) {
      console.error('Error fetching CAF Inbox processor status:', error);
      setCAFInboxStatusMessage('Failed to fetch CAF Inbox processor status');
    }
  };

  const toggleCAFInboxProcessor = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      const data = enable ? { mode: cafInboxMode } : undefined;

      await apiRequest({
        url: `/ui/admin/caf-inbox-processor/${endpoint}`,
        method: 'POST',
        data: data
      });

      setIsCAFInboxProcessorEnabled(enable);

      if (enable) {
        const modeText = cafInboxMode === 'test' ? 'TEST MODE (1 email then pause)' :
                        cafInboxMode === 'runonce' ? 'RUN ONCE MODE (1 email then pause)' :
                        'REGULAR MODE (all emails)';
        setCAFInboxStatusMessage(`CAF Inbox processor enabled in ${modeText}`);
      } else {
        setCAFInboxStatusMessage('CAF Inbox processor disabled');
      }

      fetchCAFInboxProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} CAF Inbox processor:`, error);
      setCAFInboxStatusMessage(`Failed to ${enable ? 'enable' : 'disable'} CAF Inbox processor`);
    }
  };

  const runCAFInboxBatch = async (limit: number | 'all') => {
    if (cafBatchRunning) return;
    const label = limit === 'all' ? 'entire inbox' : `${limit} emails`;
    if (limit === 'all' && !window.confirm('Process the ENTIRE unread inbox? This may take a long time and process tens of thousands of emails.')) {
      return;
    }
    setCafBatchRunning(true);
    setCAFInboxStatusMessage(`Starting CAF inbox batch run (${label})...`);
    try {
      await apiRequest({
        url: '/ui/admin/caf-inbox-processor/run-batch',
        method: 'POST',
        data: { limit },
      });
      setCAFInboxStatusMessage(`CAF inbox batch (${label}) started — running in the background. Watch the backend logs / status card for progress.`);
    } catch (error) {
      console.error('Error starting CAF inbox batch run:', error);
      setCAFInboxStatusMessage(`Failed to start CAF inbox batch run (${label})`);
    } finally {
      setCafBatchRunning(false);
    }
  };

  // --- Cosmos-backed consolidated state (pilot for caf_inbox) -----------
  const fetchCafInboxState = async () => {
    try {
      const response = await apiRequest<{ state: ProcessorStateDoc }>({
        url: '/ui/admin/processor-state/caf_inbox',
        method: 'GET',
      });
      setCafInboxState(response.state);
      setCafInboxStateError('');
    } catch (err: unknown) {
      // 404 is expected before the processor has ever claimed a lease.
      const e = err as { response?: { status?: number }; message?: string };
      if (e?.response?.status === 404) {
        setCafInboxState(null);
        setCafInboxStateError('');
      } else {
        setCafInboxStateError(e?.message || 'Failed to fetch processor state');
      }
    }
  };

  const cafInboxStateStart = async () => {
    try {
      await apiRequest({
        url: '/ui/admin/processor-state/caf_inbox/start',
        method: 'POST',
      });
      fetchCafInboxState();
    } catch (err) {
      console.error('start processor-state/caf_inbox failed:', err);
    }
  };

  const cafInboxStateStop = async () => {
    try {
      await apiRequest({
        url: '/ui/admin/processor-state/caf_inbox/stop',
        method: 'POST',
      });
      fetchCafInboxState();
    } catch (err) {
      console.error('stop processor-state/caf_inbox failed:', err);
    }
  };

  const cafInboxStateClearCrashed = async () => {
    try {
      await apiRequest({
        url: '/ui/admin/processor-state/caf_inbox/clear-crashed',
        method: 'POST',
      });
      fetchCafInboxState();
    } catch (err) {
      console.error('clear-crashed processor-state/caf_inbox failed:', err);
    }
  };

  const fetchDSARInboxProcessorStatus = async () => {
    try {
      const response = await apiRequest<{ isEnabled: boolean }>({
        url: '/ui/admin/dsar-inbox-processor',
        method: 'GET',
      });
      setIsDSARInboxProcessorEnabled(response.isEnabled);
    } catch (error) {
      console.error('Error fetching DSAR Inbox processor status:', error);
      setDSARInboxStatusMessage('Failed to fetch DSAR Inbox processor status');
    }
  };

  const toggleDSARInboxProcessor = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      const data = enable ? { mode: dsarInboxMode } : undefined;

      await apiRequest({
        url: `/ui/admin/dsar-inbox-processor/${endpoint}`,
        method: 'POST',
        data: data
      });

      setIsDSARInboxProcessorEnabled(enable);

      if (enable) {
        const modeText = dsarInboxMode === 'single' ? 'SINGLE EMAIL MODE (1 email then pause)' :
                        'REGULAR MODE (all emails every 5 mins)';
        setDSARInboxStatusMessage(`DSAR Inbox processor enabled in ${modeText}`);
      } else {
        setDSARInboxStatusMessage('DSAR Inbox processor disabled');
      }

      fetchDSARInboxProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} DSAR Inbox processor:`, error);
      setDSARInboxStatusMessage(`Failed to ${enable ? 'enable' : 'disable'} DSAR Inbox processor`);
    }
  };

  const fetchAutoLocSenderStatus = async () => {
    try {
      const response = await apiRequest<ProcessorStatusResponse>({
        url: '/ui/admin/automated-loc-sender',
        method: 'GET'
      });
      setIsAutoLocSenderEnabled(response.isEnabled);
    } catch (error) {
      console.error('Error fetching automated LOC sender status:', error);
      setAutoLocStatusMessage('Failed to fetch automated LOC sender status');
    }
  };

  const toggleAutoLocSender = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/automated-loc-sender/${endpoint}`,
        method: 'POST'
      });
      setIsAutoLocSenderEnabled(enable);
      setAutoLocStatusMessage(`Automated LOC sender ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchAutoLocSenderStatus();
      // Refresh preview after toggling
      if (enable) {
        fetchAutoLocSenderPreview();
      }
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} automated LOC sender:`, error);
      setAutoLocStatusMessage(`Failed to ${enable ? 'enable' : 'disable'} automated LOC sender`);
    }
  };

  const fetchAutoLocSenderPreview = async () => {
    try {
      const response = await apiRequest<AutoLocPreview>({
        url: '/ui/admin/automated-loc-sender/preview',
        method: 'GET'
      });
      setAutoLocPreview(response);
    } catch (error) {
      console.error('Error fetching automated LOC sender preview:', error);
      setAutoLocPreview(null);
    }
  };

  const fetchIdProcessorStatus = async () => {
    try {
      const response = await apiRequest<ProcessorStatusResponse>({
        url: '/ui/photo-id/processor/status',
        method: 'GET'
      });
      setIsIdProcessorEnabled(response.isEnabled);
      setIdProcessorStats(response.stats || null);
    } catch (error) {
      console.error('Error fetching ID processor status:', error);
      setIdProcessorStatus('Failed to fetch ID processor status');
    }
  };

  const toggleIdProcessor = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/photo-id/processor/${endpoint}`,
        method: 'POST'
      });
      setIsIdProcessorEnabled(enable);
      setIdProcessorStatus(`ID processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchIdProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} ID processor:`, error);
      setIdProcessorStatus(`Failed to ${enable ? 'enable' : 'disable'} ID processor`);
    }
  };

  const handleStartIdProcessor = async () => {
    try {
      setIdProcessorRunning(true);
      setIdProcessorStatus('Starting ID processor...');
      
      const response = await apiRequest<{
        success: boolean;
        message: string;
        processed: number;
        failed: number;
      }>({
        url: '/ui/photo-id/processor/run',
        method: 'POST'
      });
      
      if (response.success) {
        setIdProcessorStatus(`${response.message}. Processed: ${response.processed}, Failed: ${response.failed}`);
        fetchIdProcessorStatus(); // Refresh stats
      } else {
        setIdProcessorStatus(response.message || 'Failed to run ID processor');
      }
    } catch (error) {
      console.error('Error running ID processor:', error);
      setIdProcessorStatus('Failed to run ID processor');
    } finally {
      setIdProcessorRunning(false);
    }
  };

  const handleRunFullBacklog = async () => {
    if (!window.confirm(
      'This will send ID request emails to ALL eligible clients (never sent + needing reminders). ' +
      'This could be thousands of emails. Are you sure?'
    )) return;

    try {
      setBacklogProgress({ status: 'starting', message: 'Starting...', sent: 0, failed: 0, skipped: 0, total: 0, percentage: 0 });
      const response = await apiRequest<{ jobId: string }>({
        url: '/ui/photo-id/processor/run-full-backlog',
        method: 'POST',
      });
      setBacklogJobId(response.jobId);
    } catch (error) {
      console.error('Error starting full backlog:', error);
      setBacklogProgress(null);
      setIdProcessorStatus('Failed to start full backlog');
    }
  };

  // Poll backlog progress
  useEffect(() => {
    if (!backlogJobId) return;
    const interval = setInterval(async () => {
      try {
        const progress = await apiRequest<{
          status: string; message: string;
          sent: number; failed: number; skipped: number;
          total: number; percentage: number;
        }>({
          url: `/ui/photo-id/processor/backlog-progress/${backlogJobId}`,
          method: 'GET',
        });
        setBacklogProgress(progress);
        if (progress.status === 'completed' || progress.status === 'error') {
          clearInterval(interval);
          setBacklogJobId(null);
          fetchIdProcessorStatus();
        }
      } catch {
        clearInterval(interval);
        setBacklogJobId(null);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [backlogJobId]);

  const fetchGeneralProcessorsStatus = async () => {
    try {
      setIsLoadingStatus(true);
      const response = await apiRequest<{
        processors: ProcessorStatus[];
      }>({
        url: '/ui/admin/processors/status',
        method: 'GET'
      });
      setProcessorsStatus(response.processors || []);
    } catch (error) {
      console.error('Error fetching processors status:', error);
      // If endpoint doesn't exist yet, fail silently
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const toggleGeneralProcessor = async (processorName: string, enable: boolean) => {
    try {
      await apiRequest({
        url: `/ui/admin/processors/${processorName}/${enable ? 'enable' : 'disable'}`,
        method: 'POST'
      });
      // Refresh status
      fetchGeneralProcessorsStatus();
    } catch (error) {
      console.error(`Error toggling processor ${processorName}:`, error);
    }
  };

  const fetchRecentClaimsProcessorStatus = async () => {
    try {
      const response = await apiRequest<any>({
        url: '/ui/admin/processors/recent-claims/status',
        method: 'GET'
      });
      setIsRecentClaimsEnabled(response.enabled);
      setRecentClaimsStats({
        pendingClaims: response.pendingClaims || 0,
        lastRun: response.lastRun,
        nextRun: response.nextRun,
        lookbackMinutes: response.lookbackMinutes || 30,
        intervalMinutes: response.intervalMinutes || 30
      });
    } catch (error) {
      console.error('Error fetching recent claims processor status:', error);
      setRecentClaimsStatus('Failed to fetch recent claims processor status');
    }
  };

  const toggleRecentClaimsProcessor = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/processors/recent-claims/${endpoint}`,
        method: 'POST'
      });
      setIsRecentClaimsEnabled(enable);
      setRecentClaimsStatus(`Recent claims processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchRecentClaimsProcessorStatus();
      
      // Enable auto-refresh when processor is enabled
      if (enable) {
        setAutoRefreshEnabled(true);
      }
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} recent claims processor:`, error);
      setRecentClaimsStatus(`Failed to ${enable ? 'enable' : 'disable'} recent claims processor`);
    }
  };

  const handleRunRecentClaimsProcessor = async () => {
    try {
      setRecentClaimsRunning(true);
      setRecentClaimsStatus('Starting recent claims processor...');
      
      const response = await apiRequest<{
        success: boolean;
        message: string;
        lastRun?: any;
      }>({
        url: '/ui/admin/processors/recent-claims/run',
        method: 'POST'
      });
      
      if (response.success) {
        setRecentClaimsStatus(response.message);
        fetchRecentClaimsProcessorStatus(); // Refresh stats
      } else {
        setRecentClaimsStatus(response.message || 'Failed to run recent claims processor');
      }
    } catch (error) {
      console.error('Error running recent claims processor:', error);
      setRecentClaimsStatus('Failed to run recent claims processor');
    } finally {
      setRecentClaimsRunning(false);
    }
  };

  const fetchUnknownNamesCount = async () => {
    try {
      setIsCountingUnknowns(true);
      const response = await apiRequest<{ count: number; message: string }>({
        url: '/ui/admin/fix-unknown-names/count',
        method: 'GET'
      });
      setUnknownNamesCount(response.count);
    } catch (error) {
      console.error('Error fetching unknown names count:', error);
      // Fail silently if endpoint doesn't exist yet
    } finally {
      setIsCountingUnknowns(false);
    }
  };

  const fetchUnknownNamesFixStatus = async () => {
    try {
      const response = await apiRequest<UnknownNamesFixProgress>({
        url: '/ui/admin/fix-unknown-names/status',
        method: 'GET'
      });
      setUnknownNamesFixProgress(response);
    } catch (error) {
      console.error('Error fetching fix status:', error);
    }
  };

  const startUnknownNamesFix = async () => {
    try {
      const response = await apiRequest<{ success: boolean; message: string }>({
        url: '/ui/admin/fix-unknown-names/run',
        method: 'POST'
      });
      
      if (response.success) {
        // Start polling for progress
        setUnknownNamesFixProgress({
          running: true,
          total: 0,
          processed: 0,
          fixed: 0,
          no_claims: 0,
          no_name_found: 0,
          errors: 0,
          current_client: 'Starting...'
        });
        
        // Start polling
        const pollInterval = setInterval(async () => {
          await fetchUnknownNamesFixStatus();
          const status = await apiRequest<UnknownNamesFixProgress>({
            url: '/ui/admin/fix-unknown-names/status',
            method: 'GET'
          });
          
          if (!status.running) {
            clearInterval(pollInterval);
            // Refresh the count after completion
            fetchUnknownNamesCount();
          }
        }, 1000); // Poll every second
      }
    } catch (error) {
      console.error('Error starting unknown names fix:', error);
    }
  };

  const fetchIdVerificationAIStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        jobExists: boolean;
        jobRunning: boolean;
        isProduction: boolean;
        autoScheduled: boolean;
        nextRunTime?: string;
        statistics: {
          totalProcessedByAI: number;
          totalApprovedByAI: number;
          pendingTasks: number;
          approvalRate: number;
        };
      }>({
        url: '/ui/admin/id-verification-ai/status',
        method: 'GET'
      });
      setIsIdVerificationAIEnabled(response.enabled);
      setIdVerificationAIRunning(response.jobRunning);
      setIdVerificationAIStats(response.statistics);
      if (response.autoScheduled && response.nextRunTime) {
        setIdVerificationAIStatus(`Next run: ${response.nextRunTime ? new Date(response.nextRunTime).toLocaleString() : 'Not scheduled'}`);
      } else if (response.enabled && !response.autoScheduled) {
        setIdVerificationAIStatus('Enabled (Manual only - auto-run requires production environment)');
      } else {
        setIdVerificationAIStatus(response.enabled ? 'Enabled' : 'Disabled');
      }
    } catch (error) {
      console.error('Error fetching ID verification AI status:', error);
      setIdVerificationAIStatus('Failed to fetch status');
    }
  };

  const toggleIdVerificationAI = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/id-verification-ai/${endpoint}`,
        method: 'POST'
      });
      setIsIdVerificationAIEnabled(enable);
      setIdVerificationAIStatus(`ID verification AI ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchIdVerificationAIStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} ID verification AI:`, error);
      setIdVerificationAIStatus(`Failed to ${enable ? 'enable' : 'disable'} ID verification AI`);
    }
  };

  const runIdVerificationAI = async () => {
    try {
      setIdVerificationAIRunning(true);
      setIdVerificationAIStatus('Running ID verification AI processor...');
      
      const response = await apiRequest<{ success: boolean; message: string; pendingTasksRemaining: number }>({
        url: '/ui/admin/id-verification-ai/run',
        method: 'POST'
      });
      
      if (response.success) {
        setIdVerificationAIStatus(`${response.message}. Pending tasks remaining: ${response.pendingTasksRemaining}`);
        // Refresh the statistics
        fetchIdVerificationAIStatus();
      } else {
        setIdVerificationAIStatus('Failed to run ID verification AI');
      }
    } catch (error) {
      console.error('Error running ID verification AI:', error);
      setIdVerificationAIStatus('Failed to run ID verification AI processor');
    } finally {
      setIdVerificationAIRunning(false);
    }
  };

  const fetchClaimsSyncStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        interval: number;
        batchSize: number;
        jobExists: boolean;
        nextRun?: string;
        stats: {
          totalClaims: number;
          syncedClaims: number;
          unsyncedClaims: number;
          normalizedRecords: number;
          syncPercentage: number;
        };
      }>({
        url: '/ui/admin/processors/claims-sync/status',
        method: 'GET'
      });
      setIsClaimsSyncEnabled(response.enabled);
      setClaimsSyncStats(response.stats);
    } catch (error) {
      console.error('Error fetching claims sync status:', error);
      setClaimsSyncStatus('Failed to fetch claims sync status');
    }
  };

  const toggleClaimsSync = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/processors/claims-sync/${endpoint}`,
        method: 'POST'
      });
      setIsClaimsSyncEnabled(enable);
      setClaimsSyncStatus(`Claims sync ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchClaimsSyncStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} claims sync:`, error);
      setClaimsSyncStatus(`Failed to ${enable ? 'enable' : 'disable'} claims sync`);
    }
  };

  const runClaimsSync = async (syncAll: boolean = false) => {
    try {
      setClaimsSyncRunning(true);
      setClaimsSyncStatus(`Running claims sync${syncAll ? ' (sync all)' : ''}...`);

      const response = await apiRequest<{
        success: boolean;
        message: string;
        results: {
          processed: number;
          created: number;
          updated: number;
          failed: number;
          skipped: number;
        };
      }>({
        url: '/ui/admin/processors/claims-sync/run',
        method: 'POST',
        data: { syncAll }
      });

      if (response.success) {
        const { results } = response;
        setClaimsSyncStatus(
          `Sync complete! Processed: ${results.processed}, Created: ${results.created}, Updated: ${results.updated}, Failed: ${results.failed}`
        );
        fetchClaimsSyncStatus(); // Refresh stats
      } else {
        setClaimsSyncStatus(response.message || 'Failed to run claims sync');
      }
    } catch (error) {
      console.error('Error running claims sync:', error);
      setClaimsSyncStatus('Failed to run claims sync');
    } finally {
      setClaimsSyncRunning(false);
    }
  };

  const fetchCancelledClaimsSyncStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        pendingCount: number;
        syncedCount: number;
        lastRunTime?: string;
        syncPercentage: number;
      }>({
        url: '/ui/admin/cancelled-claims-sync',
        method: 'GET'
      });
      setIsCancelledClaimsSyncEnabled(response.enabled);
      setCancelledClaimsSyncStats({
        pendingCount: response.pendingCount || 0,
        syncedCount: response.syncedCount || 0,
        lastRunTime: response.lastRunTime,
        syncPercentage: response.syncPercentage || 0
      });
    } catch (error) {
      console.error('Error fetching cancelled claims sync status:', error);
      setCancelledClaimsSyncStatus('Failed to fetch cancelled claims sync status');
    }
  };

  const toggleCancelledClaimsSync = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/cancelled-claims-sync/${endpoint}`,
        method: 'POST'
      });
      setIsCancelledClaimsSyncEnabled(enable);
      setCancelledClaimsSyncStatus(`Cancelled claims sync ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchCancelledClaimsSyncStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} cancelled claims sync:`, error);
      setCancelledClaimsSyncStatus(`Failed to ${enable ? 'enable' : 'disable'} cancelled claims sync`);
    }
  };

  const runCancelledClaimsSync = async () => {
    try {
      setCancelledClaimsSyncRunning(true);
      setCancelledClaimsSyncStatus('Running cancelled claims sync...');
      
      const response = await apiRequest<{
        success: boolean;
        message: string;
        processed: number;
        synced: number;
      }>({
        url: '/ui/admin/cancelled-claims-sync/run',
        method: 'POST'
      });
      
      if (response.success) {
        setCancelledClaimsSyncStatus(`${response.message}. Processed: ${response.processed}, Synced: ${response.synced}`);
        fetchCancelledClaimsSyncStatus(); // Refresh stats
      } else {
        setCancelledClaimsSyncStatus(response.message || 'Failed to run cancelled claims sync');
      }
    } catch (error) {
      console.error('Error running cancelled claims sync:', error);
      setCancelledClaimsSyncStatus('Failed to run cancelled claims sync');
    } finally {
      setCancelledClaimsSyncRunning(false);
    }
  };

  // Client XML Export Processor functions
  const fetchClientXMLExportStatus = async () => {
    try {
      const response = await apiRequest<{
        isEnabled: boolean;
        nextRunTime?: string;
        lastRunTime?: string;
        pendingExport?: number;
        totalClients?: number;
        exportedClients?: number;
        exportPercentage?: number;
      }>({
        url: '/ui/admin/client-xml-export',
        method: 'GET'
      });
      
      setIsClientXMLExportEnabled(response.isEnabled);
      setClientXMLExportStats({
        pendingExport: response.pendingExport || 0,
        exportedClients: response.exportedClients || 0,
        totalClients: response.totalClients || 0,
        lastRunTime: response.lastRunTime,
        exportPercentage: response.exportPercentage || 0
      });
    } catch (error) {
      console.error('Error fetching client XML export status:', error);
      setClientXMLExportStatus('Failed to fetch client XML export status');
    }
  };

  const toggleClientXMLExport = async (enable: boolean) => {
    try {
      await apiRequest({
        url: `/ui/admin/client-xml-export/${enable ? 'enable' : 'disable'}`,
        method: 'POST'
      });
      setIsClientXMLExportEnabled(enable);
      setClientXMLExportStatus(enable ? 'Client XML export enabled' : 'Client XML export disabled');
      fetchClientXMLExportStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} client XML export:`, error);
      setClientXMLExportStatus(`Failed to ${enable ? 'enable' : 'disable'} client XML export`);
    }
  };

  const runClientXMLExport = async () => {
    setClientXMLExportRunning(true);
    setClientXMLExportStatus('Running client XML export...');
    
    try {
      const response = await apiRequest<{
        success: boolean;
        results?: any;
        message?: string;
      }>({
        url: '/ui/admin/client-xml-export/run',
        method: 'POST',
        data: { dryRun: false, batchSize: 50 }
      });
      
      if (response.success && response.results) {
        const { successfulExports = 0, failedExports = 0, alreadyExported = 0 } = response.results;
        setClientXMLExportStatus(
          `Export completed: ${successfulExports} exported, ${failedExports} failed, ${alreadyExported} already exported`
        );
        fetchClientXMLExportStatus(); // Refresh stats
      } else {
        setClientXMLExportStatus(response.message || 'Failed to run client XML export');
      }
    } catch (error) {
      console.error('Error running client XML export:', error);
      setClientXMLExportStatus('Failed to run client XML export');
    } finally {
      setClientXMLExportRunning(false);
    }
  };

  const testClientXMLExport = async () => {
    setClientXMLExportTestRunning(true);
    setClientXMLExportStatus('Running test export (1 client)...');
    
    try {
      const response = await apiRequest<{
        success: boolean;
        results?: any;
        message?: string;
      }>({
        url: '/ui/admin/client-xml-export/test',
        method: 'POST'
      });
      
      if (response.success) {
        const message = response.message || 'Test export completed';
        if (response.results) {
          const { successfulExports = 0, failedExports = 0, alreadyExported = 0 } = response.results;
          setClientXMLExportStatus(
            `${message} - Exported: ${successfulExports}, Failed: ${failedExports}, Already exported: ${alreadyExported}`
          );
        } else {
          setClientXMLExportStatus(message);
        }
        fetchClientXMLExportStatus(); // Refresh stats
      } else {
        setClientXMLExportStatus(response.message || 'Failed to run test export');
      }
    } catch (error) {
      console.error('Error running test client XML export:', error);
      setClientXMLExportStatus('Failed to run test export');
    } finally {
      setClientXMLExportTestRunning(false);
    }
  };

  const testPostDocumentProcessor = async () => {
    setPostDocumentRunning(true);
    setPostDocumentStatus('Processing single post document...');

    try {
      const response = await apiRequest<{
        success: boolean;
        results?: any;
        message?: string;
        error?: string;
      }>({
        url: '/ui/admin/processors/post-documents/test-run',
        method: 'POST'
      });

      if (response.success && response.results) {
        const { processed = 0, failed = 0, skipped = 0, message } = response.results;
        setPostDocumentStatus(message || `Single document processed: ${processed} processed, ${skipped} skipped, ${failed} failed`);
        fetchPostDocumentStatus();
      } else {
        setPostDocumentStatus(response.error || response.message || 'Failed to process single document');
      }
    } catch (error) {
      console.error('Error running post document single test:', error);
      setPostDocumentStatus('Failed to process single document');
    } finally {
      setPostDocumentRunning(false);
    }
  };

  const testPostDocumentProcessorVision = async () => {
    setPostDocumentVisionRunning(true);
    setPostDocumentStatus('Processing single post document with vision/OCR...');

    try {
      const response = await apiRequest<{
        success: boolean;
        results?: any;
        message?: string;
        error?: string;
      }>({
        url: '/ui/admin/processors/post-documents/test-run-vision',
        method: 'POST'
      });

      if (response.success && response.results) {
        const { processed = 0, failed = 0, skipped = 0, message } = response.results;
        setPostDocumentStatus(message || `Vision test processed: ${processed} processed, ${skipped} skipped, ${failed} failed`);
        fetchPostDocumentStatus();
      } else {
        setPostDocumentStatus(response.error || response.message || 'Failed to process single document with vision');
      }
    } catch (error) {
      console.error('Error running post document vision test:', error);
      setPostDocumentStatus('Failed to process single document with vision');
    } finally {
      setPostDocumentVisionRunning(false);
    }
  };

// Post Document Processor functions
  const fetchPostDocumentStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        pendingDocuments: number;
        lastRun?: {
          time: string;
          results?: any;
        };
        nextRun?: string;
        intervalMinutes: number;
      }>({
        url: '/ui/admin/processors/post-documents/status',
        method: 'GET'
      });
      
      setIsPostDocumentProcessorEnabled(response.enabled);
      setPostDocumentStats({
        pendingDocuments: response.pendingDocuments || 0,
        lastRun: response.lastRun,
        nextRun: response.nextRun,
        intervalMinutes: response.intervalMinutes || 10
      });
    } catch (error) {
      console.error('Error fetching post document processor status:', error);
      setPostDocumentStatus('Failed to fetch status');
    }
  };

  const togglePostDocumentProcessor = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/processors/post-documents/${endpoint}`,
        method: 'POST'
      });
      setIsPostDocumentProcessorEnabled(enable);
      setPostDocumentStatus(`Post document processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchPostDocumentStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} post document processor:`, error);
      setPostDocumentStatus(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    }
  };

  const runPostDocumentProcessor = async () => {
    setPostDocumentRunning(true);
    setPostDocumentStatus('Processing post documents...');
    
    try {
      const response = await apiRequest<{
        success: boolean;
        results?: any;
        message?: string;
      }>({
        url: '/ui/admin/processors/post-documents/run',
        method: 'POST'
      });
      
      if (response.success && response.results) {
        const { processed = 0, failed = 0, skipped = 0, auto_assigned = 0 } = response.results;
        setPostDocumentStatus(
          `Processing completed: ${processed} processed, ${auto_assigned} auto-assigned, ${skipped} skipped, ${failed} failed`
        );
        fetchPostDocumentStatus();
      } else {
        setPostDocumentStatus(response.message || 'Failed to process documents');
      }
    } catch (error) {
      console.error('Error running post document processor:', error);
      setPostDocumentStatus('Failed to run processor');
    } finally {
      setPostDocumentRunning(false);
    }
  };

  const previewPostDocuments = async () => {
    setPostDocumentPreviewing(true);
    try {
      const response = await apiRequest<{
        pendingDocuments: any[];
        count: number;
      }>({
        url: '/ui/admin/processors/post-documents/preview',
        method: 'GET'
      });

      if (response.count > 0) {
        setPostDocumentStatus(`Found ${response.count} pending documents`);
      } else {
        setPostDocumentStatus('No pending documents found');
      }
    } catch (error) {
      console.error('Error previewing post documents:', error);
      setPostDocumentStatus('Failed to preview documents');
    } finally {
      setPostDocumentPreviewing(false);
    }
  };

  // Call Tasks Processor functions
  const fetchCallTasksProcessorStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        intervalMinutes: number;
        nextRunTime?: string;
        unprocessedFilesCount: number;
      }>({
        url: '/ui/admin/call-tasks-processor',
        method: 'GET'
      });
      setIsCallTasksProcessorEnabled(response.enabled);
      setCallTasksProcessorStats(response);
    } catch (error) {
      console.error('Error fetching call tasks processor status:', error);
      setCallTasksProcessorStatus('Failed to fetch processor status');
    }
  };

  const toggleCallTasksProcessor = async (enable: boolean) => {
    try {
      setCallTasksProcessorLoading(true);
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/call-tasks-processor/${endpoint}`,
        method: 'POST'
      });
      setIsCallTasksProcessorEnabled(enable);
      setCallTasksProcessorStatus(`Processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchCallTasksProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} call tasks processor:`, error);
      setCallTasksProcessorStatus(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    } finally {
      setCallTasksProcessorLoading(false);
    }
  };

  const runCallTasksProcessor = async () => {
    try {
      setCallTasksProcessorLoading(true);
      setCallTasksProcessorStatus('Running call tasks processor...');
      const response = await apiRequest<{
        success: boolean;
        message: string;
        results: {
          tasksCreated: number;
          filesProcessed: number;
          errors: number;
        };
      }>({
        url: '/ui/admin/call-tasks-processor/run',
        method: 'POST'
      });

      if (response.success && response.results) {
        setCallTasksProcessorStatus(
          `Processor completed: ${response.results.tasksCreated} tasks created from ${response.results.filesProcessed} files`
        );
        fetchCallTasksProcessorStatus();
      } else {
        setCallTasksProcessorStatus(response.message || 'Failed to run processor');
      }
    } catch (error) {
      console.error('Error running call tasks processor:', error);
      setCallTasksProcessorStatus('Failed to run call tasks processor');
    } finally {
      setCallTasksProcessorLoading(false);
    }
  };

  const processOneCallFile = async () => {
    try {
      setCallTasksProcessorLoading(true);
      setCallTasksProcessorStatus('Processing one file...');
      const response = await apiRequest<{
        success: boolean;
        message: string;
        result?: {
          taskCreated: boolean;
          clientName?: string;
        };
      }>({
        url: '/ui/admin/call-tasks-processor/process-one',
        method: 'POST'
      });

      if (response.success && response.result) {
        const msg = response.result.taskCreated
          ? `File processed: Task created for ${response.result.clientName}`
          : `File processed: ${response.message}`;
        setCallTasksProcessorStatus(msg);
      } else {
        setCallTasksProcessorStatus(response.message);
      }
      fetchCallTasksProcessorStatus();
    } catch (error) {
      console.error('Error processing one call file:', error);
      setCallTasksProcessorStatus('Failed to process file');
    } finally {
      setCallTasksProcessorLoading(false);
    }
  };

  // CAF Backlog Processor functions
  const fetchCafBacklogProcessorStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        batchSize: number;
        lastRun: {
          time: string | null;
          results: any | null;
        };
        nextRun: string | null;
        pendingTasks: number;
        jobActive: boolean;
      }>({
        url: '/ui/admin/processors/caf-backlog/status',
        method: 'GET'
      });

      setIsCafBacklogProcessorEnabled(response.enabled);
      setCafBacklogBatchSize(response.batchSize);
      setCafBacklogStats(response);
    } catch (error) {
      console.error('Error fetching CAF backlog processor status:', error);
      setCafBacklogProcessorStatus('Failed to fetch processor status');
    }
  };

  const toggleCafBacklogProcessor = async (enable: boolean) => {
    try {
      setCafBacklogProcessorLoading(true);
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/processors/caf-backlog/${endpoint}`,
        method: 'POST'
      });
      setIsCafBacklogProcessorEnabled(enable);
      setCafBacklogProcessorStatus(`CAF Backlog Processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchCafBacklogProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} CAF backlog processor:`, error);
      setCafBacklogProcessorStatus(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    } finally {
      setCafBacklogProcessorLoading(false);
    }
  };

  const runCafBacklogProcessor = async (batchSize?: number, processAllExisting?: boolean) => {
    try {
      setCafBacklogProcessorLoading(true);
      setCafBacklogProcessorStatus(
        processAllExisting
          ? 'Processing entire CAF backlog...'
          : 'Running CAF backlog processor...'
      );

      const payload: Record<string, unknown> = {
        batchSize: batchSize || cafBacklogBatchSize
      };

      if (processAllExisting) {
        payload.processAll = true;
      }

      const response = await apiRequest<{
        success: boolean;
        message: string;
        stats?: any;
        results?: any;
      }>({
        url: '/ui/admin/processors/caf-backlog/run',
        method: 'POST',
        data: payload
      });

      const stats = response.stats || response.results;

      if (response.success && stats) {
        let summaryMessage = `Processing completed: ${stats.totalProcessed} tasks processed, ${stats.totalSuccess} successful, ${stats.totalErrors} errors`;

        if (processAllExisting && typeof stats.initialPending === 'number') {
          summaryMessage = `Processed ${stats.totalProcessed} of ${stats.initialPending} pending tasks (${stats.totalErrors} errors)`;
        }

        setCafBacklogProcessorStatus(summaryMessage);
        fetchCafBacklogProcessorStatus();
      } else {
        setCafBacklogProcessorStatus(response.message || 'Failed to run processor');
      }
    } catch (error) {
      console.error('Error running CAF backlog processor:', error);
      setCafBacklogProcessorStatus('Failed to run CAF backlog processor');
    } finally {
      setCafBacklogProcessorLoading(false);
    }
  };

  const fetchHaikuBackfillStatus = async () => {
    try {
      const response = await apiRequest<{ success: boolean; status: any }>({
        url: '/ui/admin/processors/caf-backlog/haiku-backfill/status',
        method: 'GET'
      });
      if (response.success) {
        setHaikuBackfillStatus(response.status);
      }
    } catch (error) {
      console.error('Error fetching Haiku backfill status:', error);
    }
  };

  const startHaikuBackfill = async () => {
    const confirmed = window.confirm(
      'Run the FULL CAF backlog (pending + unassigned in-progress email tasks) ' +
      'through Claude Haiku?\n\n' +
      'Estimated cost ~$250-300 for the full ~50k backlog (prompt caching does not ' +
      'apply at this prompt size) and ~20-30 hours. Runs in the background on one ' +
      'instance; you can stop it at any time - the live cost readout below tracks ' +
      'actual spend.'
    );
    if (!confirmed) return;
    try {
      setHaikuBackfillLoading(true);
      const response = await apiRequest<{ success: boolean; message?: string; error?: string }>({
        url: '/ui/admin/processors/caf-backlog/haiku-backfill/start',
        method: 'POST',
        data: {}
      });
      setCafBacklogProcessorStatus(
        response.success ? 'Haiku backfill started' : (response.error || 'Failed to start Haiku backfill')
      );
      fetchHaikuBackfillStatus();
    } catch (error: any) {
      console.error('Error starting Haiku backfill:', error);
      setCafBacklogProcessorStatus(error?.response?.data?.error || 'Failed to start Haiku backfill');
    } finally {
      setHaikuBackfillLoading(false);
    }
  };

  const stopHaikuBackfill = async () => {
    try {
      await apiRequest({ url: '/ui/admin/processors/caf-backlog/haiku-backfill/stop', method: 'POST', data: {} });
      setCafBacklogProcessorStatus('Haiku backfill stop requested');
      fetchHaikuBackfillStatus();
    } catch (error) {
      console.error('Error stopping Haiku backfill:', error);
    }
  };

  const clawbackDuplicateClaims = async (dryRun: boolean = true) => {
    try {
      setClawbackLoading(true);
      setCafBacklogProcessorStatus(dryRun ? 'Scanning for duplicate claim emails...' : 'Clawing back duplicate claim emails...');

      const response = await apiRequest<{
        success: boolean;
        message: string;
        results: {
          tasksScanned: number;
          duplicateClaimsFound: number;
          tasksReset: number;
          dryRun: boolean;
          sampleMatches: Array<{
            id: string;
            category: string;
            claimRef: string;
            emailSubject: string;
            emailFrom: string;
          }>;
        };
      }>({
        url: '/ui/admin/processors/caf-backlog/clawback-duplicate-claims',
        method: 'POST',
        data: { dryRun, batchSize: 500 }
      });

      if (response.success) {
        setClawbackResults(response.results);
        const action = dryRun ? 'Found' : 'Reset';
        setCafBacklogProcessorStatus(
          `${action} ${response.results.duplicateClaimsFound} duplicate claim emails (scanned ${response.results.tasksScanned})`
        );
      } else {
        setCafBacklogProcessorStatus(response.message || 'Clawback failed');
      }
    } catch (error) {
      console.error('Error running clawback:', error);
      setCafBacklogProcessorStatus('Failed to run duplicate claims clawback');
    } finally {
      setClawbackLoading(false);
    }
  };

  // Reset AI-classified tasks stuck in_progress so the backlog processor
  // re-runs them with the current prompt + rules. Skips human-assigned
  // tasks; preserves any existing assignedClaimId / completedBy / completedAt.
  const reprocessStuckTasks = async (dryRun: boolean = true) => {
    try {
      setReprocessStuckLoading(true);
      setCafBacklogProcessorStatus(
        dryRun ? 'Counting stuck CAF tasks…' : 'Resetting stuck CAF tasks…'
      );

      const response = await apiRequest<{
        success: boolean;
        message: string;
        results: {
          totalInProgress: number;
          inScope: number;
          humanAssignedSkipped: number;
          tasksReset: number;
          dryRun: boolean;
          preserveHumanAssigned: boolean;
          sample: Array<{
            id: string;
            aiCategory: string | null;
            aiAction: string | null;
            aiConfidence: number | null;
            assignedClaimId: string | null;
            assignedBy: string | null;
          }>;
        };
      }>({
        url: '/ui/admin/processors/caf-backlog/reprocess-stuck',
        method: 'POST',
        data: { dryRun, preserveHumanAssigned: true },
      });

      if (response.success) {
        setReprocessStuckResults(response.results);
        const verb = dryRun ? 'Would reset' : 'Reset';
        setCafBacklogProcessorStatus(
          `${verb} ${response.results.inScope} stuck tasks ` +
            `(${response.results.humanAssignedSkipped} human-assigned preserved, ` +
            `${response.results.totalInProgress} total in_progress)`
        );
      } else {
        setCafBacklogProcessorStatus(response.message || 'Reprocess failed');
      }
    } catch (error) {
      console.error('Error reprocessing stuck tasks:', error);
      setCafBacklogProcessorStatus('Failed to reprocess stuck CAF tasks');
    } finally {
      setReprocessStuckLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // CAF Embedding Processor functions (PR 5c)
  // ----------------------------------------------------------------
  const fetchCafEmbeddingProcessorStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        batchSize: number;
        intervalSeconds: number;
        lastRun: { time: string | null; results: Record<string, unknown> | null };
        nextRun: string | null;
        jobActive: boolean;
      }>({
        url: '/ui/admin/processors/caf-embedding/status',
        method: 'GET',
      });
      setIsCafEmbeddingEnabled(response.enabled);
      setCafEmbeddingStats(response);
    } catch (error) {
      console.error('Error fetching CAF embedding processor status:', error);
      setCafEmbeddingStatusMessage('Failed to fetch CAF embedding status');
    }
  };

  const toggleCafEmbeddingProcessor = async (enable: boolean) => {
    try {
      setCafEmbeddingLoading(true);
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/processors/caf-embedding/${endpoint}`,
        method: 'POST',
      });
      setIsCafEmbeddingEnabled(enable);
      setCafEmbeddingStatusMessage(
        `CAF embedding processor ${enable ? 'enabled' : 'disabled'} successfully`,
      );
      fetchCafEmbeddingProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} CAF embedding processor:`, error);
      setCafEmbeddingStatusMessage(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    } finally {
      setCafEmbeddingLoading(false);
    }
  };

  const runCafEmbeddingProcessor = async () => {
    try {
      setCafEmbeddingLoading(true);
      setCafEmbeddingStatusMessage('Running one CAF embedding tick...');
      const response = await apiRequest<{
        success: boolean;
        message: string;
        results?: Record<string, unknown>;
      }>({
        url: '/ui/admin/processors/caf-embedding/run',
        method: 'POST',
      });
      setCafEmbeddingStatusMessage(response.message || 'Embedding tick completed');
      fetchCafEmbeddingProcessorStatus();
    } catch (error) {
      console.error('Error running CAF embedding job:', error);
      setCafEmbeddingStatusMessage('Failed to run CAF embedding job');
    } finally {
      setCafEmbeddingLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // CAF Corpus Cluster Processor functions (PR 5c)
  // ----------------------------------------------------------------
  const fetchCafCorpusClusterProcessorStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        intervalSeconds: number;
        cosineThreshold: number;
        minClusterSize: number;
        lastRun: { time: string | null; results: Record<string, unknown> | null };
        nextRun: string | null;
        jobActive: boolean;
      }>({
        url: '/ui/admin/processors/caf-corpus-cluster/status',
        method: 'GET',
      });
      setIsCafCorpusClusterEnabled(response.enabled);
      setCafCorpusClusterStats(response);
    } catch (error) {
      console.error('Error fetching CAF corpus cluster processor status:', error);
      setCafCorpusClusterStatusMessage('Failed to fetch CAF corpus cluster status');
    }
  };

  const toggleCafCorpusClusterProcessor = async (enable: boolean) => {
    try {
      setCafCorpusClusterLoading(true);
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/processors/caf-corpus-cluster/${endpoint}`,
        method: 'POST',
      });
      setIsCafCorpusClusterEnabled(enable);
      setCafCorpusClusterStatusMessage(
        `CAF corpus cluster processor ${enable ? 'enabled' : 'disabled'} successfully`,
      );
      fetchCafCorpusClusterProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} CAF corpus cluster processor:`, error);
      setCafCorpusClusterStatusMessage(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    } finally {
      setCafCorpusClusterLoading(false);
    }
  };

  const runCafCorpusClusterProcessor = async () => {
    try {
      setCafCorpusClusterLoading(true);
      setCafCorpusClusterStatusMessage('Running one corpus clustering pass...');
      const response = await apiRequest<{
        success: boolean;
        message: string;
        results?: Record<string, unknown>;
      }>({
        url: '/ui/admin/processors/caf-corpus-cluster/run',
        method: 'POST',
      });
      setCafCorpusClusterStatusMessage(response.message || 'Clustering pass completed');
      fetchCafCorpusClusterProcessorStatus();
    } catch (error) {
      console.error('Error running CAF corpus cluster job:', error);
      setCafCorpusClusterStatusMessage('Failed to run CAF corpus cluster job');
    } finally {
      setCafCorpusClusterLoading(false);
    }
  };

  // Post Backlog Processor functions
  const fetchPostBacklogProcessorStatus = async () => {
    try {
      const response = await apiRequest<{
        enabled: boolean;
        totalTasks: number;
        pendingTasks: number;
        lastRun: {
          time: string | null;
          results: {
            totalProcessed: number;
            totalSuccess: number;
            totalErrors: number;
            categoryChanged: number;
            automationsTriggered: number;
            belowThresholdCount: number;
            categoryCounts: Record<string, number>;
            processingTimeMs: number;
          } | null;
        };
        jobActive: boolean;
      }>({
        url: '/ui/admin/processors/post-backlog/status',
        method: 'GET'
      });

      setIsPostBacklogProcessorEnabled(response.enabled);
      setPostBacklogStats(response);
    } catch (error) {
      console.error('Error fetching post backlog processor status:', error);
      setPostBacklogProcessorStatus('Failed to fetch processor status');
    }
  };

  const togglePostBacklogProcessor = async (enable: boolean) => {
    try {
      setPostBacklogProcessorLoading(true);
      const endpoint = enable ? 'enable' : 'disable';
      await apiRequest({
        url: `/ui/admin/processors/post-backlog/${endpoint}`,
        method: 'POST'
      });
      setIsPostBacklogProcessorEnabled(enable);
      setPostBacklogProcessorStatus(`Post Backlog Processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchPostBacklogProcessorStatus();
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} post backlog processor:`, error);
      setPostBacklogProcessorStatus(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    } finally {
      setPostBacklogProcessorLoading(false);
    }
  };

  const runPostBacklogProcessor = async (batchSize?: number, processAll?: boolean) => {
    try {
      setPostBacklogProcessorLoading(true);
      setPostBacklogProcessorStatus(
        processAll
          ? 'Processing entire post backlog...'
          : 'Running post backlog processor...'
      );

      const payload: Record<string, unknown> = {
        batchSize: batchSize || postBacklogBatchSize
      };

      if (processAll) {
        payload.processAll = true;
      }

      const response = await apiRequest<{
        success: boolean;
        message: string;
        stats?: any;
        results?: any;
      }>({
        url: '/ui/admin/processors/post-backlog/run',
        method: 'POST',
        data: payload
      });

      const stats = response.stats || response.results;

      if (response.success && stats) {
        let summaryMessage = `Processing completed: ${stats.totalProcessed} tasks processed, ${stats.totalSuccess} successful, ${stats.totalErrors} errors`;

        if (stats.categoryChanged > 0) {
          summaryMessage += `, ${stats.categoryChanged} categories changed`;
        }
        if (stats.automationsTriggered > 0) {
          summaryMessage += `, ${stats.automationsTriggered} automations triggered`;
        }

        setPostBacklogProcessorStatus(summaryMessage);
        fetchPostBacklogProcessorStatus();
      } else {
        setPostBacklogProcessorStatus(response.message || 'Failed to run processor');
      }
    } catch (error) {
      console.error('Error running post backlog processor:', error);
      setPostBacklogProcessorStatus('Failed to run post backlog processor');
    } finally {
      setPostBacklogProcessorLoading(false);
    }
  };

  const runPostBacklogDryRun = async (batchSize?: number) => {
    try {
      setPostBacklogProcessorLoading(true);
      setPostBacklogProcessorStatus('Running dry-run preview...');

      const response = await apiRequest<{
        success: boolean;
        message: string;
        preview: Array<any>;
        totalTasks: number;
      }>({
        url: '/ui/admin/processors/post-backlog/dry-run',
        method: 'POST',
        data: { batchSize: batchSize || 10 }
      });

      if (response.success) {
        setPostBacklogDryRunResults(response.preview);
        const changed = response.preview.filter((p: any) => p.changed).length;
        setPostBacklogProcessorStatus(
          `Dry-run complete: ${response.preview.length} tasks previewed, ${changed} would change category`
        );
      } else {
        setPostBacklogProcessorStatus(response.message || 'Dry-run failed');
      }
    } catch (error) {
      console.error('Error running post backlog dry-run:', error);
      setPostBacklogProcessorStatus('Failed to run dry-run');
    } finally {
      setPostBacklogProcessorLoading(false);
    }
  };

  // Auto-refresh effect for recent claims processor
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (autoRefreshEnabled && isRecentClaimsEnabled) {
      // Refresh every minute
      interval = setInterval(() => {
        fetchRecentClaimsProcessorStatus();
      }, 60000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefreshEnabled, isRecentClaimsEnabled]);

  return (
    <div className="processors-management">
      <h2>Processors Management</h2>
      
      {/* Client-Claim Processing Status */}
      <ProcessingStatus />
      
      {/* Email Cancellation Processor */}
      <div className="processor-section">
        <h3>Email Cancellation Processor</h3>
        <ProcessorStatePanel processorId="email_cancellation" state={processorStates['email_cancellation']} />
        <p className="description">Automatically processes cancellation emails from the inbox that are {emailProcessorDays}+ days old.</p>
        
        <div className="processor-config">
          <div className="form-group">
            <label htmlFor="emailProcessorDays">
              Process emails older than:
              <input 
                id="emailProcessorDays"
                type="number" 
                min="1" 
                max="30" 
                value={emailProcessorDays} 
                onChange={(e) => setEmailProcessorDays(parseInt(e.target.value) || 3)}
                style={{
                  width: '60px',
                  padding: '4px 8px',
                  margin: '0 8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              days
            </label>
            <button 
              onClick={updateProcessorConfig}
              className="export-btn"
              style={{ marginLeft: '10px', backgroundColor: '#6c757d' }}
            >
              Update Config
            </button>
          </div>
        </div>
        
        <div className="button-group">
          <button 
            onClick={() => toggleProcessor(true)}
            disabled={isProcessorEnabled} 
            className="enable-btn"
          >
            Enable Processor
          </button>
          
          <button 
            onClick={() => toggleProcessor(false)}
            disabled={!isProcessorEnabled} 
            className="disable-btn"
          >
            Disable Processor
          </button>

          <button
            onClick={runProcessorNow}
            className="secondary-btn"
            disabled={runningProcessorNow}
          >
            {runningProcessorNow ? 'Running…' : 'Run Once Now'}
          </button>
        </div>
        
        {statusMessage && (
          <div className="status-message">{statusMessage}</div>
        )}
      </div>

      {/* CAF Inbox Processor */}
      <div className="processor-section">
        <h3>CAF Inbox Processor</h3>
        {/* The "Consolidated state" panel below already shows the heartbeat
            and controls — no separate badge needed at the heading. */}
        <p className="description">Processes incoming CAF inbox emails to automatically update claim statuses and extract relevant information.</p>
        
        {/* Mode selection */}
        <div className="mode-selection" style={{ marginBottom: '15px' }}>
          <label>Processing Mode: </label>
          <select 
            value={cafInboxMode} 
            onChange={(e) => setCAFInboxMode(e.target.value as 'regular' | 'test' | 'runonce')}
            disabled={isCAFInboxProcessorEnabled}
            style={{ marginLeft: '10px', padding: '5px' }}
          >
            <option value="test">Test Mode (1 email then pause)</option>
            <option value="runonce">Run Once Mode (1 email then pause)</option>
            <option value="regular">Regular Mode (all emails, production)</option>
          </select>
        </div>
        
        <div className="button-group">
          <button
            onClick={() => toggleCAFInboxProcessor(true)}
            disabled={isCAFInboxProcessorEnabled}
            className="enable-btn"
          >
            Enable CAF Inbox Processor
          </button>
          <button
            onClick={() => toggleCAFInboxProcessor(false)}
            disabled={!isCAFInboxProcessorEnabled}
            className="disable-btn"
          >
            Disable CAF Inbox Processor
          </button>
        </div>

        {/* One-off manual runs. Each runs the same processor as the scheduler
            but with a custom email cap. Shares the same distributed lease, so
            if the scheduler is currently running a tick this will skip. */}
        <div className="button-group" style={{ marginTop: '10px' }}>
          <button
            onClick={() => runCAFInboxBatch(10)}
            disabled={cafBatchRunning}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Process 10 Emails
          </button>
          <button
            onClick={() => runCAFInboxBatch(1000)}
            disabled={cafBatchRunning}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Process 1000 Emails
          </button>
          <button
            onClick={() => runCAFInboxBatch('all')}
            disabled={cafBatchRunning}
            className="export-btn"
            style={{ backgroundColor: '#dc3545' }}
          >
            Process Entire Inbox
          </button>
        </div>

        {cafInboxStatusMessage && (
          <div className="status-message">{cafInboxStatusMessage}</div>
        )}

        {/* --- Cosmos-backed state (pilot). Side-by-side with legacy above. --- */}
        <div
          className="processor-state-card"
          style={{
            marginTop: '18px',
            padding: '14px',
            background: '#fafbfc',
            border: '1px solid #dde1e6',
            borderRadius: '6px',
          }}
        >
          <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#374151' }}>
            Consolidated state (multi-instance, Cosmos-backed)
          </h4>
          {cafInboxStateError && (
            <div style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '8px' }}>
              {cafInboxStateError}
            </div>
          )}
          {!cafInboxState && !cafInboxStateError && (
            <div style={{ color: '#6b7280', fontSize: '13px' }}>
              No state recorded yet (processor has not claimed a lease).
            </div>
          )}
          {cafInboxState && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backgroundColor:
                      cafInboxState.actualState === 'running' ? '#d1fae5' :
                      cafInboxState.actualState === 'starting' ? '#fef3c7' :
                      cafInboxState.actualState === 'finishing' ? '#fef3c7' :
                      cafInboxState.actualState === 'crashed' ? '#fee2e2' :
                      cafInboxState.actualState === 'stopped' ? '#e5e7eb' :
                      '#e5e7eb',
                    color:
                      cafInboxState.actualState === 'running' ? '#065f46' :
                      cafInboxState.actualState === 'crashed' ? '#991b1b' :
                      '#374151',
                  }}
                >
                  {cafInboxState.actualState.toUpperCase()}
                </span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  desired: <strong>{cafInboxState.desiredState}</strong>
                </span>
                {cafInboxState.ownerInstanceId && (
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>
                    owner: {cafInboxState.ownerInstanceId}
                  </span>
                )}
                {cafInboxState.ownerBootId && (
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af' }}>
                    boot: {cafInboxState.ownerBootId.substring(0, 8)}
                  </span>
                )}
              </div>

              <div style={{ marginTop: '10px', fontSize: '13px', color: '#4b5563' }}>
                <div>
                  <strong>Last heartbeat:</strong>{' '}
                  <span
                    style={{
                      color: (() => {
                        if (!cafInboxState.lastHeartbeatAt) return '#6b7280';
                        const ageMs = Date.now() - new Date(cafInboxState.lastHeartbeatAt).getTime();
                        if (ageMs > 300_000) return '#b91c1c'; // red after 5min
                        if (ageMs > 60_000) return '#b45309';  // amber after 60s
                        return '#065f46';                       // green
                      })(),
                    }}
                  >
                    {formatRelativeTime(cafInboxState.lastHeartbeatAt)}
                  </span>
                </div>
                {cafInboxState.currentPhase && (
                  <div>
                    <strong>Phase:</strong> {cafInboxState.currentPhase}
                  </div>
                )}
                {cafInboxState.startedAt && (
                  <div>
                    <strong>Started:</strong> {new Date(cafInboxState.startedAt).toLocaleString()}{' '}
                    {cafInboxState.startedBy && <em>by {cafInboxState.startedBy}</em>}
                  </div>
                )}
                {cafInboxState.lastError && (
                  <div style={{ color: '#b91c1c', marginTop: '4px' }}>
                    <strong>Last error:</strong> {cafInboxState.lastError}
                  </div>
                )}
                {cafInboxState.lastWarning && (
                  <div style={{ color: '#b45309', marginTop: '4px' }}>
                    <strong>Warning:</strong> {cafInboxState.lastWarning}
                  </div>
                )}
              </div>

              {cafInboxState.lastRunStats && (
                <details
                  open={showCafLastRunStats}
                  onToggle={(e) => setShowCafLastRunStats((e.target as HTMLDetailsElement).open)}
                  style={{ marginTop: '10px' }}
                >
                  <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                    Last run stats
                  </summary>
                  <pre
                    style={{
                      margin: '6px 0 0 0',
                      padding: '8px',
                      background: '#f3f4f6',
                      borderRadius: '4px',
                      fontSize: '12px',
                      overflowX: 'auto',
                    }}
                  >
                    {JSON.stringify(cafInboxState.lastRunStats, null, 2)}
                  </pre>
                </details>
              )}
            </>
          )}

          <div className="button-group" style={{ marginTop: '12px' }}>
            <button
              onClick={cafInboxStateStart}
              className="enable-btn"
              disabled={cafInboxState?.desiredState === 'running'}
            >
              Start (request)
            </button>
            <button
              onClick={cafInboxStateStop}
              className="disable-btn"
              disabled={cafInboxState?.desiredState === 'stopped'}
            >
              Stop (request)
            </button>
            <button
              onClick={cafInboxStateClearCrashed}
              className="secondary-btn"
              disabled={cafInboxState?.actualState !== 'crashed'}
            >
              Clear crashed
            </button>
            <button
              onClick={fetchCafInboxState}
              className="export-btn"
              style={{ backgroundColor: '#17a2b8' }}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* DSAR Inbox Processor */}
      <div className="processor-section">
        <h3>DSAR Inbox Processor</h3>
        <ProcessorStatePanel processorId="dsar_inbox" state={processorStates['dsar_inbox']} />
        <p className="description">Processes incoming DSAR emails from dsar@complexlaw.co.uk to create tasks for data subject access requests.</p>

        {/* Mode selection */}
        <div className="mode-selection" style={{ marginBottom: '15px' }}>
          <label>Processing Mode: </label>
          <select
            value={dsarInboxMode}
            onChange={(e) => setDSARInboxMode(e.target.value as 'regular' | 'single')}
            disabled={isDSARInboxProcessorEnabled}
            style={{ marginLeft: '10px', padding: '5px' }}
          >
            <option value="single">Single Email Mode (1 email then pause)</option>
            <option value="regular">Regular Mode (all emails every 5 mins)</option>
          </select>
        </div>

        <div className="button-group">
          <button
            onClick={() => toggleDSARInboxProcessor(true)}
            disabled={isDSARInboxProcessorEnabled}
            className="enable-btn"
          >
            Enable DSAR Inbox Processor
          </button>
          <button
            onClick={() => toggleDSARInboxProcessor(false)}
            disabled={!isDSARInboxProcessorEnabled}
            className="disable-btn"
          >
            Disable DSAR Inbox Processor
          </button>
        </div>
        {dsarInboxStatusMessage && (
          <div className="status-message">{dsarInboxStatusMessage}</div>
        )}
      </div>

      {/* Automated LOC Sender */}
      <div className="processor-section">
        <h3>Automated LOC Sender</h3>
        <ProcessorStatePanel processorId="automated_loc_sender" state={processorStates['automated_loc_sender']} />
        <p className="description">Automatically sends Letters of Claim to eligible clients on a regular schedule.</p>
        
        {autoLocPreview && (
          <div className="loc-preview">
            <h4>Current Eligible Claims:</h4>
            <ul>
              <li><strong>{autoLocPreview.totalEligible}</strong> total claims need LOCs</li>
              <li><strong>{autoLocPreview.withValidEmails}</strong> have valid lender email addresses</li>
              {autoLocPreview.withoutEmails > 0 && (
                <li><strong>{autoLocPreview.withoutEmails}</strong> missing lender email addresses (will be skipped)</li>
              )}
              <li>Next run will process up to <strong>{autoLocPreview.nextBatchSize}</strong> claims (max {autoLocPreview.maxPerRun} per run)</li>
            </ul>
          </div>
        )}
        
        <div className="button-group">
          <button
            onClick={() => toggleAutoLocSender(true)}
            disabled={isAutoLocSenderEnabled}
            className="enable-btn"
          >
            Enable Automated LOC Sender
          </button>
          <button
            onClick={() => toggleAutoLocSender(false)}
            disabled={!isAutoLocSenderEnabled}
            className="disable-btn"
          >
            Disable Automated LOC Sender
          </button>
          <button
            onClick={fetchAutoLocSenderPreview}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Refresh Preview
          </button>
        </div>
        {autoLocStatusMessage && (
          <div className="status-message">{autoLocStatusMessage}</div>
        )}
      </div>


      {/* Photo ID Request Processor */}
      <div className="processor-section">
        <h3>Photo ID Request Processor</h3>
        <ProcessorStatePanel processorId="photo_id_request" state={processorStates['photo_id_request']} />
        <p className="description">Automatically sends photo ID verification requests to clients with claims that have LOCs sent.</p>
        
        {idProcessorStats && (
          <div className="loc-preview">
            <h4>Current Status:</h4>
            <ul>
              <li><strong>{idProcessorStats.totalPending}</strong> clients pending ID verification</li>
              <li><strong>{idProcessorStats.processedToday}</strong> requests sent today</li>
              {idProcessorStats.lastRunTime && (
                <li>Last run: <strong>{idProcessorStats.lastRunTime ? new Date(idProcessorStats.lastRunTime).toLocaleString() : 'Never'}</strong></li>
              )}
            </ul>
          </div>
        )}
        
        <div className="button-group">
          <button
            onClick={() => handleStartIdProcessor()}
            disabled={idProcessorRunning || !isIdProcessorEnabled}
            className="enable-btn"
          >
            {idProcessorRunning ? 'Processing...' : 'Start ID Processor'}
          </button>
          <button
            onClick={() => toggleIdProcessor(true)}
            disabled={isIdProcessorEnabled}
            className="enable-btn"
          >
            Enable ID Processor
          </button>
          <button
            onClick={() => toggleIdProcessor(false)}
            disabled={!isIdProcessorEnabled}
            className="disable-btn"
          >
            Disable ID Processor
          </button>
          <button
            onClick={fetchIdProcessorStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Refresh Status
          </button>
          <button
            onClick={handleRunFullBacklog}
            disabled={!!backlogJobId}
            className="enable-btn"
            style={{ backgroundColor: '#e67e22' }}
          >
            {backlogJobId ? 'Backlog Running...' : 'Process Full Backlog'}
          </button>
        </div>
        {idProcessorStatus && (
          <div className="status-message">{idProcessorStatus}</div>
        )}

        {backlogProgress && (
          <div className="loc-preview" style={{ marginTop: '10px' }}>
            <h4>Full Backlog: {backlogProgress.status === 'completed' ? 'Complete' : backlogProgress.status === 'error' ? 'Error' : 'Running'}</h4>
            <p>{backlogProgress.message}</p>
            {backlogProgress.total > 0 && (
              <>
                <div className="progress-bar-container" style={{ background: '#e9ecef', borderRadius: '4px', height: '20px', marginBottom: '8px' }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${backlogProgress.percentage}%`,
                      background: backlogProgress.status === 'error' ? '#dc3545' : '#28a745',
                      height: '100%',
                      borderRadius: '4px',
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                <ul>
                  <li><strong>{backlogProgress.sent.toLocaleString()}</strong> sent</li>
                  <li><strong>{backlogProgress.skipped.toLocaleString()}</strong> skipped (already verified)</li>
                  <li><strong>{backlogProgress.failed.toLocaleString()}</strong> failed</li>
                  <li><strong>{backlogProgress.percentage}%</strong> of {backlogProgress.total.toLocaleString()} clients</li>
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      {/* Unknown Names Fix -- hidden (retired pending ownership review). */}
      {SHOW_RETIRED_PROCESSOR_SECTIONS && (
      <div className="processor-section">
        <h3>Unknown Names Fix</h3>
        <p className="description">Fix client records that have "Unknown" as first or last name by extracting names from their claims data.</p>
        
        {unknownNamesCount !== null && (
          <div className="loc-preview">
            <h4>Current Status:</h4>
            <ul>
              <li><strong>{unknownNamesCount?.toLocaleString() || '0'}</strong> clients with Unknown names</li>
            </ul>
          </div>
        )}
        
        {unknownNamesFixProgress && (
          <div className="fix-progress">
            <h4>{unknownNamesFixProgress.running ? 'Processing...' : 'Last Run Results'}</h4>
            
            {unknownNamesFixProgress.running && (
              <div className="progress-bar-container">
                <div 
                  className="progress-bar" 
                  style={{ 
                    width: `${unknownNamesFixProgress.total > 0 ? (unknownNamesFixProgress.processed / unknownNamesFixProgress.total * 100) : 0}%` 
                  }}
                />
                <span className="progress-text">
                  {unknownNamesFixProgress.processed} / {unknownNamesFixProgress.total} 
                  ({unknownNamesFixProgress.total > 0 ? Math.round(unknownNamesFixProgress.processed / unknownNamesFixProgress.total * 100) : 0}%)
                </span>
              </div>
            )}
            
            <div className="fix-stats">
              <p><strong>Current:</strong> {unknownNamesFixProgress.current_client}</p>
              <p><strong>Fixed:</strong> {unknownNamesFixProgress.fixed}</p>
              <p><strong>No claims found:</strong> {unknownNamesFixProgress.no_claims}</p>
              <p><strong>No name in claims:</strong> {unknownNamesFixProgress.no_name_found}</p>
              {unknownNamesFixProgress.errors > 0 && (
                <p className="error-stat"><strong>Errors:</strong> {unknownNamesFixProgress.errors}</p>
              )}
              {unknownNamesFixProgress.last_error && (
                <p className="error-message">Last error: {unknownNamesFixProgress.last_error}</p>
              )}
            </div>
          </div>
        )}
        
        <div className="button-group">
          <button
            onClick={startUnknownNamesFix}
            disabled={unknownNamesFixProgress?.running || unknownNamesCount === 0}
            className="enable-btn"
          >
            {unknownNamesFixProgress?.running ? 'Running...' : 'Run Fix Process'}
          </button>
          <button
            onClick={fetchUnknownNamesCount}
            disabled={isCountingUnknowns}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            {isCountingUnknowns ? 'Counting...' : 'Refresh Count'}
          </button>
        </div>
      </div>
      )}

      {/* Cancelled Claims Sync Processor -- hidden (retired pending ownership review). */}
      {SHOW_RETIRED_PROCESSOR_SECTIONS && (
      <div className="processor-section">
        <h3>Cancelled Claims Sync Processor</h3>
        <p>Status: {isCancelledClaimsSyncEnabled ? 'Enabled' : 'Disabled'}</p>
        <p className="description">Automatically syncs cancelled claims (after 01/09/25) to the ClaimsGate API for processing.</p>
        
        {cancelledClaimsSyncStats && (
          <div className="processor-stats">
            <h4>Statistics:</h4>
            <ul>
              <li><strong>{cancelledClaimsSyncStats.pendingCount}</strong> claims pending sync</li>
              <li><strong>{cancelledClaimsSyncStats.syncedCount}</strong> claims synced</li>
              <li><strong>{cancelledClaimsSyncStats.syncPercentage}%</strong> sync completion</li>
              {cancelledClaimsSyncStats.lastRunTime && (
                <li>Last run: <strong>{new Date(cancelledClaimsSyncStats.lastRunTime).toLocaleString()}</strong></li>
              )}
            </ul>
          </div>
        )}
        
        <div className="button-group">
          <button
            onClick={() => toggleCancelledClaimsSync(true)}
            disabled={isCancelledClaimsSyncEnabled}
            className="enable-btn"
          >
            Enable Sync
          </button>
          <button
            onClick={() => toggleCancelledClaimsSync(false)}
            disabled={!isCancelledClaimsSyncEnabled}
            className="disable-btn"
          >
            Disable Sync
          </button>
          <button
            onClick={runCancelledClaimsSync}
            disabled={!isCancelledClaimsSyncEnabled || cancelledClaimsSyncRunning}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
          >
            {cancelledClaimsSyncRunning ? 'Running...' : 'Run Now'}
          </button>
          <button
            onClick={fetchCancelledClaimsSyncStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Refresh Status
          </button>
        </div>
        
        {cancelledClaimsSyncStatus && (
          <div className="status-message">{cancelledClaimsSyncStatus}</div>
        )}
      </div>
      )}

      {/* Client XML Export Processor */}
      <div className="processor-section">
        <h3>Client XML Export Processor</h3>
        <p>Status: {isClientXMLExportEnabled ? 'Enabled' : 'Disabled'}</p>
        <p className="description">Exports client data to XML format and stores in Azure Blob Storage.</p>
        
        {clientXMLExportStats && (
          <div className="processor-stats">
            <h4>Export Statistics:</h4>
            <ul>
              <li><strong>{clientXMLExportStats.pendingExport}</strong> clients pending export</li>
              <li><strong>{clientXMLExportStats.exportedClients}</strong> of {clientXMLExportStats.totalClients} clients exported ({clientXMLExportStats.exportPercentage}%)</li>
              {clientXMLExportStats.lastRunTime && (
                <li>Last run: {new Date(clientXMLExportStats.lastRunTime).toLocaleString()}</li>
              )}
            </ul>
          </div>
        )}
        
        <div className="button-group">
          <button
            onClick={() => toggleClientXMLExport(true)}
            disabled={isClientXMLExportEnabled}
            className="enable-btn"
          >
            Enable Export
          </button>
          <button
            onClick={() => toggleClientXMLExport(false)}
            disabled={!isClientXMLExportEnabled}
            className="disable-btn"
          >
            Disable Export
          </button>
          <button
            onClick={runClientXMLExport}
            disabled={!isClientXMLExportEnabled || clientXMLExportRunning}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
          >
            {clientXMLExportRunning ? 'Running...' : 'Run Now'}
          </button>
          <button
            onClick={testClientXMLExport}
            disabled={clientXMLExportTestRunning || clientXMLExportRunning}
            className="export-btn"
            style={{ backgroundColor: '#ffc107' }}
            title="Test export with 1 client - doesn't require processor to be enabled"
          >
            {clientXMLExportTestRunning ? 'Testing...' : 'Test Export (1 Client)'}
          </button>
          <button
            onClick={fetchClientXMLExportStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Refresh Stats
          </button>
        </div>
        
        {clientXMLExportStatus && (
          <div className="status-message">{clientXMLExportStatus}</div>
        )}
      </div>

      {/* Post Document Processor */}
      <div className="processor-section">
        <h3>Post Document Processor</h3>
        <ProcessorStatePanel processorId="post_document" state={processorStates['post_document']} />
        <p className="description">Processes PDF documents from Azure Blob Storage and creates tasks with automatic claim assignment.</p>
        
        {postDocumentStats && (
          <div className="processor-stats">
            <h4>Processor Statistics:</h4>
            <ul>
              <li><strong>{postDocumentStats.pendingDocuments}</strong> documents pending processing</li>
              {postDocumentStats.lastRun && postDocumentStats.lastRun.time && (
                <li>Last run: {new Date(postDocumentStats.lastRun.time).toLocaleString()}</li>
              )}
              {postDocumentStats.lastRun && postDocumentStats.lastRun.results && (
                <li>Last run results: {postDocumentStats.lastRun.results.processed || 0} processed, {postDocumentStats.lastRun.results.auto_assigned || 0} auto-assigned</li>
              )}
              {postDocumentStats.nextRun && (
                <li>Next scheduled run: {new Date(postDocumentStats.nextRun).toLocaleString()}</li>
              )}
              <li>Run interval: Every {postDocumentStats.intervalMinutes} minutes</li>
            </ul>
          </div>
        )}
        
        <div className="button-group">
          <button
            onClick={() => togglePostDocumentProcessor(true)}
            disabled={isPostDocumentProcessorEnabled}
            className="enable-btn"
          >
            Enable Processor
          </button>
          <button
            onClick={() => togglePostDocumentProcessor(false)}
            disabled={!isPostDocumentProcessorEnabled}
            className="disable-btn"
          >
            Disable Processor
          </button>
          <button
            onClick={runPostDocumentProcessor}
            disabled={postDocumentRunning || postDocumentVisionRunning}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
          >
            {postDocumentRunning ? 'Processing...' : 'Process Now'}
          </button>
          <button
            onClick={testPostDocumentProcessor}
            disabled={postDocumentRunning || postDocumentVisionRunning}
            className="export-btn"
            style={{ backgroundColor: '#ffc107', color: '#1f2933' }}
          >
            Test Single
          </button>
          <button
            onClick={testPostDocumentProcessorVision}
            disabled={postDocumentRunning || postDocumentVisionRunning}
            className="export-btn"
            style={{ backgroundColor: '#f97316', color: '#1f2933' }}
          >
            Test Single (Vision)
          </button>
          <button
            onClick={previewPostDocuments}
            className="export-btn"
            disabled={postDocumentPreviewing || postDocumentRunning || postDocumentVisionRunning}
            style={{ backgroundColor: '#17a2b8' }}
          >
            {postDocumentPreviewing ? 'Previewing...' : 'Preview Pending'}
          </button>
          <button
            onClick={fetchPostDocumentStatus}
            className="export-btn"
            style={{ backgroundColor: '#6c757d' }}
          >
            Refresh Stats
          </button>
        </div>
        
        {postDocumentStatus && (
          <div className="status-message">{postDocumentStatus}</div>
        )}
      </div>

      {/* Call Tasks Processor -- hidden (retired pending ownership review). */}
      {SHOW_RETIRED_PROCESSOR_SECTIONS && (
      <div className="processor-section">
        <h3>Call Tasks Processor</h3>
        <p>Status: {isCallTasksProcessorEnabled ? 'Enabled' : 'Disabled'}</p>
        <p className="description">Automatically processes call recordings from Google Cloud Storage, transcribes them using OpenAI Whisper, and creates review tasks.</p>

        {callTasksProcessorStats && (
          <div className="processor-stats">
            <h4>Processor Information:</h4>
            <ul>
              <li><strong>{callTasksProcessorStats.unprocessedFilesCount}</strong> unprocessed files in GCS</li>
              <li>Run interval: Every <strong>{callTasksProcessorStats.intervalMinutes}</strong> minutes</li>
              {callTasksProcessorStats.nextRunTime && (
                <li>Next scheduled run: {new Date(callTasksProcessorStats.nextRunTime).toLocaleString()}</li>
              )}
            </ul>
          </div>
        )}

        <div className="button-group">
          <button
            onClick={() => toggleCallTasksProcessor(true)}
            disabled={isCallTasksProcessorEnabled || callTasksProcessorLoading}
            className="enable-btn"
          >
            Enable Processor
          </button>
          <button
            onClick={() => toggleCallTasksProcessor(false)}
            disabled={!isCallTasksProcessorEnabled || callTasksProcessorLoading}
            className="disable-btn"
          >
            Disable Processor
          </button>
          <button
            onClick={runCallTasksProcessor}
            disabled={callTasksProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
          >
            {callTasksProcessorLoading ? 'Running...' : 'Run Now'}
          </button>
          <button
            onClick={processOneCallFile}
            disabled={callTasksProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#ffc107', color: '#1f2933' }}
            title="Process a single file for testing"
          >
            Process One File (Test)
          </button>
          <button
            onClick={fetchCallTasksProcessorStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
            disabled={callTasksProcessorLoading}
          >
            Refresh Status
          </button>
        </div>

        {callTasksProcessorStatus && (
          <div className="status-message">{callTasksProcessorStatus}</div>
        )}
      </div>
      )}

      {/* CAF Backlog Processor */}
      <div className="processor-section">
        <h3>CAF Backlog Processor</h3>
        <ProcessorStatePanel processorId="caf_backlog" state={processorStates['caf_backlog']} readOnly={cafSwitchMoved} />
        {cafSwitchMoved && aiSwitch.value && <AutomationSwitchNotice mode={aiSwitch.value.mode} />}
        <p className="description">
          Processes pending CAF inbox email tasks using AI classification. Automatically categorizes emails,
          extracts claim information, matches claims, updates key dates, and takes appropriate actions (archive, assign, or manual review).
        </p>

        {cafBacklogStats && (
          <>
            <div className="processor-stats">
              <h4>Processor Configuration:</h4>
              <ul>
                <li><strong>{cafBacklogStats.pendingTasks}</strong> pending tasks in queue</li>
                <li>Batch size: <strong>{cafBacklogStats.batchSize}</strong> tasks per run</li>
                {cafBacklogStats.nextRun && (
                  <li>Next scheduled run: <strong>{new Date(cafBacklogStats.nextRun).toLocaleString()}</strong></li>
                )}
                {cafBacklogStats.lastRun?.time && (
                  <li>Last run: <strong>{new Date(cafBacklogStats.lastRun.time).toLocaleString()}</strong></li>
                )}
              </ul>
            </div>

            {cafBacklogStats.lastRun?.results && (
              <div className="processor-stats" style={{ marginTop: '15px', backgroundColor: '#e8f5e9' }}>
                <h4>Last Run Statistics:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '10px' }}>
                  <div>
                    <p><strong>Processing:</strong></p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>{cafBacklogStats.lastRun.results.totalProcessed} total processed</li>
                      <li>{cafBacklogStats.lastRun.results.totalSuccess} successful</li>
                      <li>{cafBacklogStats.lastRun.results.totalErrors} errors</li>
                      <li>{(cafBacklogStats.lastRun.results.processingTimeMs / 1000).toFixed(2)}s processing time</li>
                    </ul>
                  </div>

                  <div>
                    <p><strong>Outcomes:</strong></p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>{cafBacklogStats.lastRun.results.claimsMatched} claims matched</li>
                      <li>{cafBacklogStats.lastRun.results.tasksArchived} tasks archived</li>
                      <li>{cafBacklogStats.lastRun.results.tasksAssigned} tasks assigned</li>
                      <li>{cafBacklogStats.lastRun.results.tasksCompleted} tasks completed</li>
                      <li>{cafBacklogStats.lastRun.results.keyDatesUpdated} key dates updated</li>
                    </ul>
                  </div>

                  <div>
                    <p><strong>AI Actions:</strong></p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>Auto Archive: {cafBacklogStats.lastRun.results.actionCounts?.AUTO_ARCHIVE || 0}</li>
                      <li>Auto Assign+Complete: {cafBacklogStats.lastRun.results.actionCounts?.AUTO_ASSIGN_COMPLETE || 0}</li>
                      <li>Auto Assign Only: {cafBacklogStats.lastRun.results.actionCounts?.AUTO_ASSIGN_ONLY || 0}</li>
                      <li>Manual Review: {cafBacklogStats.lastRun.results.actionCounts?.MANUAL_REVIEW || 0}</li>
                    </ul>
                  </div>

                  <div>
                    <p><strong>AI Usage:</strong></p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>{cafBacklogStats.lastRun.results.totalTokens?.toLocaleString() || 0} tokens used</li>
                      <li>{(cafBacklogStats.lastRun.results.totalTokens * 0.000002).toFixed(4)} estimated cost</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!cafSwitchMoved && <div className="processor-config" style={{ marginTop: '15px' }}>
          <div className="form-group">
            <label htmlFor="cafBacklogBatchSize">
              Batch Size:
              <input
                id="cafBacklogBatchSize"
                type="number"
                min="1"
                max="500"
                value={cafBacklogBatchSize}
                onChange={(e) => setCafBacklogBatchSize(parseInt(e.target.value) || 100)}
                disabled={isCafBacklogProcessorEnabled}
                style={{
                  width: '80px',
                  padding: '4px 8px',
                  margin: '0 8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              tasks per run
            </label>
            {isCafBacklogProcessorEnabled && (
              <span style={{ marginLeft: '10px', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                (Disable processor to change batch size)
              </span>
            )}
          </div>
        </div>}

        <div className="button-group">
          {!cafSwitchMoved && <>
          <button
            onClick={() => toggleCafBacklogProcessor(true)}
            disabled={isCafBacklogProcessorEnabled || cafBacklogProcessorLoading}
            className="enable-btn"
          >
            Enable Processor
          </button>
          <button
            onClick={() => toggleCafBacklogProcessor(false)}
            disabled={!isCafBacklogProcessorEnabled || cafBacklogProcessorLoading}
            className="disable-btn"
          >
            Disable Processor
          </button>
          <button
            onClick={() => runCafBacklogProcessor()}
            disabled={cafBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
            title="Process tasks with configured batch size"
          >
            {cafBacklogProcessorLoading ? 'Running...' : 'Run Now'}
          </button>
          <button
            onClick={() => runCafBacklogProcessor(10)}
            disabled={cafBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#ffc107', color: '#1f2933' }}
            title="Process 10 tasks for testing"
          >
            Test Run (10 tasks)
          </button>
          <button
            onClick={() => runCafBacklogProcessor(1)}
            disabled={cafBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#fd7e14', color: '#1f2933' }}
            title="Process single task for testing"
          >
            Single Task Test
          </button>
          <button
            onClick={() => runCafBacklogProcessor(undefined, true)}
            disabled={cafBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#b91c1c' }}
            title="Process every pending CAF task right now"
          >
            Process Entire Backlog
          </button>
          <button
            onClick={startHaikuBackfill}
            disabled={haikuBackfillLoading || haikuBackfillStatus?.running}
            className="export-btn"
            style={{ backgroundColor: '#7c3aed' }}
            title="Run the full backlog (pending + unassigned in-progress) through Claude Haiku"
          >
            {haikuBackfillStatus?.running ? 'Haiku Backfill Running…' : 'Run Backlog via Claude Haiku'}
          </button>
          {haikuBackfillStatus?.running && (
            <button
              onClick={stopHaikuBackfill}
              className="export-btn"
              style={{ backgroundColor: '#6b7280' }}
              title="Stop after the in-flight task"
            >
              Stop Haiku Backfill
            </button>
          )}
          </>}
          <button
            onClick={fetchCafBacklogProcessorStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
            disabled={cafBacklogProcessorLoading}
          >
            Refresh Status
          </button>
        </div>

        {haikuBackfillStatus && (haikuBackfillStatus.running || haikuBackfillStatus.processed > 0) && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f3ff', borderRadius: '8px', border: '1px solid #7c3aed', fontSize: '13px' }}>
            <strong>Claude Haiku backfill</strong>{' '}
            {haikuBackfillStatus.running ? '— running' : '— finished'}
            {haikuBackfillStatus.stopRequested && haikuBackfillStatus.running ? ' (stopping…)' : ''}
            <div style={{ marginTop: '6px' }}>
              {haikuBackfillStatus.processed}/{haikuBackfillStatus.totalTasks} processed
              {' · '}{haikuBackfillStatus.success} ok · {haikuBackfillStatus.errors} errors · {haikuBackfillStatus.skipped} skipped
              {' · '}{haikuBackfillStatus.remaining} remaining
            </div>
            <div>
              Tokens: {(haikuBackfillStatus.usage?.inputTokens ?? 0).toLocaleString()} in
              {' / '}{(haikuBackfillStatus.usage?.outputTokens ?? 0).toLocaleString()} out
              {' / '}{(haikuBackfillStatus.usage?.cacheReadTokens ?? 0).toLocaleString()} cached
              {' · '}Est. cost ${haikuBackfillStatus.estimatedCostUsd?.toFixed(2)}
              {' · '}Haiku calls: {haikuBackfillStatus.llmCalls ?? 0}
            </div>
            {haikuBackfillStatus.lastError && (
              <div style={{ color: '#b91c1c' }}>Last error: {haikuBackfillStatus.lastError}</div>
            )}
          </div>
        )}

        {/* Clawback Section */}
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>Clawback Duplicate Claim Emails</h4>
          <p style={{ fontSize: '13px', color: '#856404', margin: '0 0 15px 0' }}>
            Find and reset completed emails that should have been flagged as duplicate claims
            (lender said complaint already exists and did NOT log a new one).
          </p>
          <div className="button-group">
            <button
              onClick={() => clawbackDuplicateClaims(true)}
              disabled={clawbackLoading || cafBacklogProcessorLoading}
              className="export-btn"
              style={{ backgroundColor: '#ffc107', color: '#1f2933' }}
              title="Preview which emails would be reset (dry run)"
            >
              {clawbackLoading ? 'Scanning...' : 'Preview Clawback'}
            </button>
            <button
              onClick={() => clawbackDuplicateClaims(false)}
              disabled={clawbackLoading || cafBacklogProcessorLoading || !clawbackResults?.duplicateClaimsFound}
              className="export-btn"
              style={{ backgroundColor: '#dc3545' }}
              title="Actually reset the emails to manual review"
            >
              Execute Clawback
            </button>
          </div>

          {clawbackResults && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>{clawbackResults.dryRun ? 'Preview:' : 'Completed:'}</strong>{' '}
                {clawbackResults.duplicateClaimsFound} duplicate claim emails found
                {!clawbackResults.dryRun && ` (${clawbackResults.tasksReset} reset)`}
              </p>
              {clawbackResults.sampleMatches.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 5px 0', fontSize: '13px' }}><strong>Sample matches:</strong></p>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', maxHeight: '150px', overflowY: 'auto' }}>
                    {clawbackResults.sampleMatches.map((match, idx) => (
                      <li key={idx} style={{ marginBottom: '5px' }}>
                        <strong>{match.claimRef}</strong> - {match.emailFrom}<br/>
                        <span style={{ color: '#666' }}>{match.emailSubject}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Reprocess Stuck Tasks Section */}
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #4a90e2' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#1f3a68' }}>Reprocess Stuck Tasks (Re-classify with Current Rules)</h4>
          <p style={{ fontSize: '13px', color: '#1f3a68', margin: '0 0 15px 0' }}>
            Reset AI-classified CAF tasks that are still sitting in manual review (<code>in_progress</code>)
            so the backlog processor re-runs them with the current prompt + rules. Run this after a prompt
            or rule change so previously-stuck tasks get a fresh decision.
            <br />
            <strong>Safer than “Reset AI Processed”:</strong> tasks where a complexlaw.co.uk user manually
            assigned the claim are skipped (their work is preserved), and existing <code>assignedClaimId</code> /
            <code>completedBy</code> / <code>completedAt</code> values are left intact — only the AI fields and
            status are reset so the re-classifier can confirm or override.
          </p>
          <div className="button-group">
            <button
              onClick={() => reprocessStuckTasks(true)}
              disabled={reprocessStuckLoading || cafBacklogProcessorLoading}
              className="export-btn"
              style={{ backgroundColor: '#4a90e2' }}
              title="Preview how many tasks would be reset (dry run)"
            >
              {reprocessStuckLoading ? 'Counting…' : 'Preview Reprocess'}
            </button>
            <button
              onClick={() => reprocessStuckTasks(false)}
              disabled={
                reprocessStuckLoading ||
                cafBacklogProcessorLoading ||
                !reprocessStuckResults?.inScope
              }
              className="export-btn"
              style={{ backgroundColor: '#dc3545' }}
              title="Reset the in-scope tasks back to pending so the backlog processor picks them up again"
            >
              Execute Reprocess
            </button>
          </div>

          {reprocessStuckResults && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>{reprocessStuckResults.dryRun ? 'Preview:' : 'Completed:'}</strong>{' '}
                <strong>{reprocessStuckResults.inScope}</strong> tasks
                {' '}{reprocessStuckResults.dryRun ? 'would be' : 'were'} reset to pending
                {!reprocessStuckResults.dryRun && ` (${reprocessStuckResults.tasksReset} actually updated)`}.
                <br />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Total in_progress + AI-processed: {reprocessStuckResults.totalInProgress.toLocaleString()}.
                  Human-assigned skipped (preserved): {reprocessStuckResults.humanAssignedSkipped.toLocaleString()}.
                </span>
              </p>
              {reprocessStuckResults.sample.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 5px 0', fontSize: '13px' }}><strong>Sample (10 most recent):</strong></p>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', maxHeight: '150px', overflowY: 'auto' }}>
                    {reprocessStuckResults.sample.map((row) => (
                      <li key={row.id} style={{ marginBottom: '5px' }}>
                        <strong>{row.aiCategory || '?'}</strong> ({row.aiAction || '?'},{' '}
                        {row.aiConfidence != null ? (row.aiConfidence * 100).toFixed(0) + '%' : '–'})
                        {row.assignedClaimId && (
                          <> → claim <strong>{row.assignedClaimId}</strong></>
                        )}
                        {row.assignedBy && <span style={{ color: '#666' }}> by {row.assignedBy}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {cafBacklogProcessorStatus && (
          <div className="status-message">{cafBacklogProcessorStatus}</div>
        )}
      </div>

      {/* CAF Embedding Processor (PR 5c) */}
      <div className="processor-section">
        <h3>CAF Embedding Processor</h3>
        <p>Status: {isCafEmbeddingEnabled ? 'Enabled' : 'Disabled'}</p>
        <p className="description">
          Polls <code>email_tasks</code> for rows that don't yet have an embedding in
          <code> caf_email_embeddings</code> and embeds them via Azure OpenAI (text-embedding-3-small).
          Feeds the RAG retrieval layer used by the CAF classifier. Embedding only — never writes corpus labels.
        </p>

        <ProcessorStatePanel processorId="caf_email_embedding_job" state={processorStates['caf_email_embedding_job']} />

        {cafEmbeddingStats && (
          <div className="processor-stats">
            <h4>Processor Configuration:</h4>
            <ul>
              <li>Batch size: <strong>{cafEmbeddingStats.batchSize}</strong> tasks per tick</li>
              <li>Interval: <strong>{cafEmbeddingStats.intervalSeconds}</strong> seconds</li>
              {cafEmbeddingStats.nextRun && (
                <li>Next scheduled run: <strong>{new Date(cafEmbeddingStats.nextRun).toLocaleString()}</strong></li>
              )}
              {cafEmbeddingStats.lastRun?.time && (
                <li>Last run: <strong>{new Date(cafEmbeddingStats.lastRun.time).toLocaleString()}</strong></li>
              )}
              {cafEmbeddingStats.lastRun?.results && (
                <li>
                  Last result: <code style={{ fontSize: '12px' }}>{JSON.stringify(cafEmbeddingStats.lastRun.results)}</code>
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="button-group">
          <button
            onClick={() => toggleCafEmbeddingProcessor(true)}
            disabled={isCafEmbeddingEnabled || cafEmbeddingLoading}
            className="enable-btn"
          >
            Enable Processor
          </button>
          <button
            onClick={() => toggleCafEmbeddingProcessor(false)}
            disabled={!isCafEmbeddingEnabled || cafEmbeddingLoading}
            className="disable-btn"
          >
            Disable Processor
          </button>
          <button
            onClick={runCafEmbeddingProcessor}
            disabled={cafEmbeddingLoading}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
            title="Run one embedding tick now"
          >
            {cafEmbeddingLoading ? 'Running...' : 'Run Once'}
          </button>
          <button
            onClick={fetchCafEmbeddingProcessorStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
            disabled={cafEmbeddingLoading}
          >
            Refresh Status
          </button>
        </div>

        {cafEmbeddingStatusMessage && (
          <div className="status-message">{cafEmbeddingStatusMessage}</div>
        )}
      </div>

      {/* CAF Corpus Cluster Processor (PR 5c) */}
      <div className="processor-section">
        <h3>CAF Corpus Cluster Processor</h3>
        <p>Status: {isCafCorpusClusterEnabled ? 'Enabled' : 'Disabled'}</p>
        <p className="description">
          Nightly job that groups <code>caf_email_embeddings</code> within-sender by cosine similarity
          and writes clusters to <code>caf_email_clusters</code> for staff to browse and label in the
          CAF Corpus Manager. Idempotent. Never writes labels — only similarity groupings.
        </p>

        <ProcessorStatePanel processorId="caf_corpus_cluster_job" state={processorStates['caf_corpus_cluster_job']} />

        {cafCorpusClusterStats && (
          <div className="processor-stats">
            <h4>Processor Configuration:</h4>
            <ul>
              <li>Interval: <strong>{cafCorpusClusterStats.intervalSeconds}</strong> seconds (~{Math.round(cafCorpusClusterStats.intervalSeconds / 3600)}h)</li>
              <li>Cosine threshold: <strong>{cafCorpusClusterStats.cosineThreshold}</strong></li>
              <li>Minimum cluster size: <strong>{cafCorpusClusterStats.minClusterSize}</strong></li>
              {cafCorpusClusterStats.nextRun && (
                <li>Next scheduled run: <strong>{new Date(cafCorpusClusterStats.nextRun).toLocaleString()}</strong></li>
              )}
              {cafCorpusClusterStats.lastRun?.time && (
                <li>Last run: <strong>{new Date(cafCorpusClusterStats.lastRun.time).toLocaleString()}</strong></li>
              )}
              {cafCorpusClusterStats.lastRun?.results && (
                <li>
                  Last result: <code style={{ fontSize: '12px' }}>{JSON.stringify(cafCorpusClusterStats.lastRun.results)}</code>
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="button-group">
          <button
            onClick={() => toggleCafCorpusClusterProcessor(true)}
            disabled={isCafCorpusClusterEnabled || cafCorpusClusterLoading}
            className="enable-btn"
          >
            Enable Processor
          </button>
          <button
            onClick={() => toggleCafCorpusClusterProcessor(false)}
            disabled={!isCafCorpusClusterEnabled || cafCorpusClusterLoading}
            className="disable-btn"
          >
            Disable Processor
          </button>
          <button
            onClick={runCafCorpusClusterProcessor}
            disabled={cafCorpusClusterLoading}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
            title="Run one clustering pass now"
          >
            {cafCorpusClusterLoading ? 'Running...' : 'Run Once'}
          </button>
          <button
            onClick={fetchCafCorpusClusterProcessorStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
            disabled={cafCorpusClusterLoading}
          >
            Refresh Status
          </button>
        </div>

        {cafCorpusClusterStatusMessage && (
          <div className="status-message">{cafCorpusClusterStatusMessage}</div>
        )}
      </div>

      {/* Post Backlog Processor */}
      <div className="processor-section">
        <h3>Post Backlog Processor</h3>
        <ProcessorStatePanel processorId="post_backlog" state={processorStates['post_backlog']} />
        <p className="description">
          Reclassifies existing post tasks using improved AI classification. Processes both completed and pending tasks,
          flags category changes, and triggers automations for eligible pending tasks.
        </p>

        {postBacklogStats && (
          <>
            <div className="processor-stats">
              <h4>Processor Configuration:</h4>
              <ul>
                <li><strong>{postBacklogStats.totalTasks}</strong> total post tasks</li>
                <li><strong>{postBacklogStats.pendingTasks}</strong> pending tasks</li>
                {postBacklogStats.lastRun?.time && (
                  <li>Last run: <strong>{new Date(postBacklogStats.lastRun.time).toLocaleString()}</strong></li>
                )}
              </ul>
            </div>

            {postBacklogStats.lastRun?.results && (
              <div className="processor-stats" style={{ marginTop: '15px', backgroundColor: '#e8f5e9' }}>
                <h4>Last Run Statistics:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '10px' }}>
                  <div>
                    <p><strong>Processing:</strong></p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>{postBacklogStats.lastRun.results.totalProcessed} total processed</li>
                      <li>{postBacklogStats.lastRun.results.totalSuccess} successful</li>
                      <li>{postBacklogStats.lastRun.results.totalErrors} errors</li>
                      <li>{(postBacklogStats.lastRun.results.processingTimeMs / 1000).toFixed(2)}s processing time</li>
                    </ul>
                  </div>

                  <div>
                    <p><strong>Reclassification:</strong></p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>{postBacklogStats.lastRun.results.categoryChanged} categories changed</li>
                      <li>{postBacklogStats.lastRun.results.automationsTriggered} automations triggered</li>
                      <li>{postBacklogStats.lastRun.results.belowThresholdCount} below confidence threshold</li>
                    </ul>
                  </div>

                  {postBacklogStats.lastRun.results.categoryCounts && Object.keys(postBacklogStats.lastRun.results.categoryCounts).length > 0 && (
                    <div>
                      <p><strong>Category Breakdown:</strong></p>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {Object.entries(postBacklogStats.lastRun.results.categoryCounts).map(([cat, count]) => (
                          <li key={cat}>{cat}: {count}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="processor-config" style={{ marginTop: '15px' }}>
          <div className="form-group">
            <label htmlFor="postBacklogBatchSize">
              Batch Size:
              <input
                id="postBacklogBatchSize"
                type="number"
                min="1"
                max="500"
                value={postBacklogBatchSize}
                onChange={(e) => setPostBacklogBatchSize(parseInt(e.target.value) || 100)}
                disabled={isPostBacklogProcessorEnabled}
                style={{
                  width: '80px',
                  padding: '4px 8px',
                  margin: '0 8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              tasks per run
            </label>
            {isPostBacklogProcessorEnabled && (
              <span style={{ marginLeft: '10px', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                (Disable processor to change batch size)
              </span>
            )}
          </div>
        </div>

        <div className="button-group">
          <button
            onClick={() => togglePostBacklogProcessor(true)}
            disabled={isPostBacklogProcessorEnabled || postBacklogProcessorLoading}
            className="enable-btn"
          >
            Enable Processor
          </button>
          <button
            onClick={() => togglePostBacklogProcessor(false)}
            disabled={!isPostBacklogProcessorEnabled || postBacklogProcessorLoading}
            className="disable-btn"
          >
            Disable Processor
          </button>
          <button
            onClick={() => runPostBacklogProcessor()}
            disabled={postBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
            title="Process tasks with configured batch size"
          >
            {postBacklogProcessorLoading ? 'Running...' : 'Run Now'}
          </button>
          <button
            onClick={() => runPostBacklogProcessor(10)}
            disabled={postBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#ffc107', color: '#1f2933' }}
            title="Process 10 tasks for testing"
          >
            Test Run (10 tasks)
          </button>
          <button
            onClick={() => runPostBacklogProcessor(1)}
            disabled={postBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#fd7e14', color: '#1f2933' }}
            title="Process single task for testing"
          >
            Single Task Test
          </button>
          <button
            onClick={() => runPostBacklogDryRun(20)}
            disabled={postBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#6f42c1' }}
            title="Preview reclassification without writing changes"
          >
            Dry Run Preview (20)
          </button>
          <button
            onClick={() => runPostBacklogProcessor(undefined, true)}
            disabled={postBacklogProcessorLoading}
            className="export-btn"
            style={{ backgroundColor: '#b91c1c' }}
            title="Process every post task right now"
          >
            Process Entire Backlog
          </button>
          <button
            onClick={fetchPostBacklogProcessorStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
            disabled={postBacklogProcessorLoading}
          >
            Refresh Status
          </button>
        </div>

        {/* Dry Run Results */}
        {postBacklogDryRunResults && postBacklogDryRunResults.length > 0 && (
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f3e8ff', borderRadius: '8px', border: '1px solid #6f42c1' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#4a1d96' }}>Dry Run Preview Results</h4>
            <p style={{ fontSize: '13px', color: '#4a1d96', margin: '0 0 10px 0' }}>
              {postBacklogDryRunResults.filter(r => r.changed).length} of {postBacklogDryRunResults.length} tasks would change category
            </p>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e9d5ff', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #6f42c1' }}>Task ID</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #6f42c1' }}>Status</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #6f42c1' }}>Old Category</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #6f42c1' }}>New Category</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '2px solid #6f42c1' }}>Confidence</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '2px solid #6f42c1' }}>Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {postBacklogDryRunResults.map((result, idx) => (
                    <tr key={idx} style={{ backgroundColor: result.changed ? '#fef3c7' : result.error ? '#fee2e2' : 'transparent' }}>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}>{result.id?.substring(0, 8)}...</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}>{result.status}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}>{result.oldCategory || '-'}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}>
                        {result.error ? <span style={{ color: '#dc2626' }}>{result.error}</span> : result.newCategory}
                        {result.belowThreshold && <span style={{ color: '#d97706', marginLeft: '4px' }} title="Below confidence threshold">(low)</span>}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                        {result.newConfidence != null ? `${(result.newConfidence * 100).toFixed(0)}%` : '-'}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                        {result.changed ? <span style={{ color: '#d97706', fontWeight: 'bold' }}>Yes</span> : 'No'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={() => setPostBacklogDryRunResults(null)}
              style={{ marginTop: '10px', padding: '4px 12px', fontSize: '12px', background: 'transparent', border: '1px solid #6f42c1', borderRadius: '4px', color: '#6f42c1', cursor: 'pointer' }}
            >
              Dismiss
            </button>
          </div>
        )}

        {postBacklogProcessorStatus && (
          <div className="status-message">{postBacklogProcessorStatus}</div>
        )}
      </div>

      {/* General Processors Status */}
      {/* ID Verification AI */}
      <div className="processor-section">
        <h3>ID Verification AI</h3>
        <p>Status: {isIdVerificationAIEnabled ? 'Enabled' : 'Disabled'}</p>
        <p className="description">Automatically verifies photo IDs using OpenAI Vision. Auto-approves IDs with 95%+ confidence.</p>
        
        {idVerificationAIStats && (
          <div className="processor-stats">
            <h4>Statistics:</h4>
            <ul>
              <li><strong>{idVerificationAIStats.totalProcessedByAI}</strong> IDs processed by AI</li>
              <li><strong>{idVerificationAIStats.totalApprovedByAI}</strong> auto-approved ({idVerificationAIStats.approvalRate}%)</li>
              <li><strong>{idVerificationAIStats.pendingTasks}</strong> pending tasks</li>
            </ul>
          </div>
        )}
        
        <div className="button-group">
          <button
            onClick={() => toggleIdVerificationAI(true)}
            disabled={isIdVerificationAIEnabled}
            className="enable-btn"
          >
            Enable AI Verification
          </button>
          <button
            onClick={() => toggleIdVerificationAI(false)}
            disabled={!isIdVerificationAIEnabled}
            className="disable-btn"
          >
            Disable AI Verification
          </button>
          <button
            onClick={runIdVerificationAI}
            disabled={!isIdVerificationAIEnabled || idVerificationAIRunning}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
          >
            {idVerificationAIRunning ? 'Running...' : 'Run Now'}
          </button>
        </div>
        
        {idVerificationAIStatus && (
          <div className="status-message">{idVerificationAIStatus}</div>
        )}
      </div>

      {/* Claims Sync Processor */}
      <div className="processor-section">
        <h3>Claims Sync Processor</h3>
        <p>Status: {isClaimsSyncEnabled ? 'Enabled' : 'Disabled'}</p>
        <p className="description">Synchronizes claims data from the main claims container to the claims_normalized container. Keeps normalized claims up-to-date with status changes, cancellations, and other modifications.</p>

        {claimsSyncStats && (
          <div className="processor-stats">
            <h4>Sync Statistics:</h4>
            <ul>
              <li><strong>{claimsSyncStats.totalClaims}</strong> total claims in source container</li>
              <li><strong>{claimsSyncStats.syncedClaims}</strong> claims synced ({claimsSyncStats.syncPercentage}%)</li>
              <li><strong>{claimsSyncStats.unsyncedClaims}</strong> claims pending sync</li>
              <li><strong>{claimsSyncStats.normalizedRecords}</strong> normalized records</li>
            </ul>
          </div>
        )}

        <div className="button-group">
          <button
            onClick={() => toggleClaimsSync(true)}
            disabled={isClaimsSyncEnabled}
            className="enable-btn"
          >
            Enable Sync
          </button>
          <button
            onClick={() => toggleClaimsSync(false)}
            disabled={!isClaimsSyncEnabled}
            className="disable-btn"
          >
            Disable Sync
          </button>
          <button
            onClick={() => runClaimsSync(false)}
            disabled={claimsSyncRunning}
            className="export-btn"
            style={{ backgroundColor: '#28a745' }}
          >
            {claimsSyncRunning ? 'Running...' : 'Run Now (New Only)'}
          </button>
          <button
            onClick={() => runClaimsSync(true)}
            disabled={claimsSyncRunning}
            className="export-btn"
            style={{ backgroundColor: '#ffc107' }}
          >
            {claimsSyncRunning ? 'Running...' : 'Sync All (Update Existing)'}
          </button>
          <button
            onClick={fetchClaimsSyncStatus}
            className="export-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Refresh Status
          </button>
        </div>

        {claimsSyncStatus && (
          <div className="status-message">{claimsSyncStatus}</div>
        )}
      </div>

      {processorsStatus.length > 0 && (
        <div className="processor-section">
          <h3>All Processors Status</h3>
          <p className="description">Overview of all system processors including Claim and ID verification processors.</p>
          
          <div className="processors-grid">
            {processorsStatus.map((processor) => (
              <div key={processor.name} className="processor-card">
                <div className="processor-header">
                  <h4>{processor.name}</h4>
                  <span className={`status-badge ${processor.enabled ? 'enabled' : 'disabled'}`}>
                    {processor.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                
                {processor.description && (
                  <p className="processor-description">{processor.description}</p>
                )}
                
                {processor.configuration && (
                  <div className="processor-config-details">
                    {processor.configuration.rateLimit && (
                      <div className="config-item">
                        <span className="config-label">Rate Limit:</span>
                        <span className="config-value">{processor.configuration.rateLimit}</span>
                      </div>
                    )}
                    {processor.configuration.minAgeDays && (
                      <div className="config-item">
                        <span className="config-label">Min Age:</span>
                        <span className="config-value">{processor.configuration.minAgeDays} days</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="processor-actions">
                  <button
                    onClick={() => toggleGeneralProcessor(processor.name, !processor.enabled)}
                    className={processor.enabled ? 'disable-btn' : 'enable-btn'}
                  >
                    {processor.enabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <button
            onClick={fetchGeneralProcessorsStatus}
            className="export-btn"
            style={{ marginTop: '15px' }}
            disabled={isLoadingStatus}
          >
            {isLoadingStatus ? 'Loading...' : 'Refresh All Status'}
          </button>
        </div>
      )}

      {/* Legacy CSV Import Section -- hidden (retired pending ownership review). */}
      {SHOW_RETIRED_PROCESSOR_SECTIONS && <LegacyCSVImport />}

      <style>{`
        .processors-management {
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .processor-section {
          margin-bottom: 30px;
          padding: 20px;
          border: 1px solid #ddd;
          border-radius: 5px;
        }
        
        .processor-section .description {
          color: #666;
          font-style: italic;
          font-size: 14px;
          margin: 5px 0 15px 0;
        }
        
        .processor-config {
          margin: 15px 0;
        }
        
        .form-group {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        }
        
        .form-group label {
          display: flex;
          align-items: center;
        }
        
        .loc-preview, .processor-stats {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 15px;
          margin: 15px 0;
        }
        
        .loc-preview h4, .processor-stats h4 {
          margin: 0 0 10px 0;
          color: #495057;
          font-size: 16px;
        }
        
        .loc-preview ul, .processor-stats ul {
          margin: 0;
          padding-left: 20px;
        }
        
        .loc-preview li, .processor-stats li {
          margin: 5px 0;
          color: #6c757d;
        }
        
        .button-group {
          display: flex;
          gap: 10px;
          margin: 15px 0;
        }
        
        .enable-btn, .disable-btn, .export-btn, .secondary-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .enable-btn {
          background-color: #28a745;
          color: white;
        }

        .disable-btn {
          background-color: #dc3545;
          color: white;
        }

        .export-btn {
          background-color: #007bff;
          color: white;
        }

        .secondary-btn {
          background-color: #6c757d;
          color: #ffffff;
        }

        .enable-btn:disabled, .disable-btn:disabled, .export-btn:disabled, .secondary-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        
        .status-message {
          margin-top: 15px;
          padding: 10px;
          border-radius: 4px;
          background-color: #f8f9fa;
          border-left: 4px solid #007bff;
        }
        
        .processors-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }
        
        .processor-card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
          transition: box-shadow 0.2s;
        }
        
        .processor-card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        .processor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        
        .processor-header h4 {
          margin: 0;
          color: #333;
        }
        
        .status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .status-badge.enabled {
          background-color: #d4edda;
          color: #155724;
        }
        
        .status-badge.disabled {
          background-color: #f8d7da;
          color: #721c24;
        }
        
        .processor-description {
          color: #666;
          font-size: 14px;
          margin: 10px 0;
        }
        
        .processor-config-details {
          background: white;
          padding: 10px;
          border-radius: 4px;
          margin: 10px 0;
        }
        
        .config-item {
          display: flex;
          justify-content: space-between;
          margin: 5px 0;
          font-size: 13px;
        }
        
        .config-label {
          color: #666;
          font-weight: 500;
        }
        
        .config-value {
          color: #333;
        }
        
        .processor-actions {
          margin-top: 15px;
        }
        
        .fix-progress {
          background-color: #f0f8ff;
          border: 1px solid #b0d4ff;
          border-radius: 6px;
          padding: 15px;
          margin: 15px 0;
        }
        
        .fix-progress h4 {
          margin: 0 0 15px 0;
          color: #0066cc;
        }
        
        .progress-bar-container {
          position: relative;
          background-color: #e0e0e0;
          border-radius: 10px;
          height: 30px;
          margin-bottom: 15px;
          overflow: hidden;
        }
        
        .progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background-color: #4CAF50;
          transition: width 0.3s ease;
        }
        
        .progress-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-weight: bold;
          color: #333;
          z-index: 1;
        }
        
        .fix-stats {
          margin-top: 10px;
        }
        
        .fix-stats p {
          margin: 5px 0;
          color: #555;
        }
        
        .error-stat {
          color: #d32f2f !important;
        }
        
        .error-message {
          color: #d32f2f !important;
          font-style: italic;
          margin-top: 10px !important;
        }
      `}</style>
    </div>
  );
};

export default ProcessorsManagement;
